/* eslint-disable */
import { useState, useEffect, useRef, useMemo } from "react";
import emailjs from '@emailjs/browser';
import { CarFront, ScrollText, UserRound, Settings, Zap, CalendarDays } from "lucide-react";
import {
  functions, httpsCallable,
  auth, db, storage, loadGoogleMaps, requestNotificationPermission, uploadFile, calcDistanceETA, ORDER_CLEAR_DRIVER,
  getMatchStatusAfterConfirm, shouldPromoteScheduledPending,
  collection, addDoc, setDoc, serverTimestamp, onSnapshot, query, where, doc, updateDoc, getDoc, getDocs, orderBy, limit, Timestamp, increment,
  RecaptchaVerifier, signInWithPhoneNumber, signInWithEmailAndPassword, sendPasswordResetEmail,
} from "./firebase";
import {
  TEAL,GOLD,GREEN,RED,WARN,BG,WHITE,TEXT,SUB,LIGHT,DC,
  Btn,Inp,Sel,Card,Bar,Wrap,Sec,Toggle,Tag,StarRating,StarDisplay,InterestPicker,InterestTags,
  AddressInput,MapPicker,GPSBtn,MapView,ChatBox,BottomNav,TabBar,LoadingOverlay,Empty,StatusBadge,PeerProfileModal,
  formatDate,formatTime,formatDateTime,
  OCCUPATION_PRESETS,EXPERTISE_PRESETS,HOBBY_PRESETS,
} from "./components";
import {
  weekdaysSummary, nextRunDatesForWeekdays, formatRunDateLabel, ROUTE_WEEKDAY_LABELS,
  todayIsoLocal, routeScheduleSummary,
} from "./routeHelpers";
import { torontoCarpoolReferenceBand } from "./torontoCarpoolReference";
import { useLanguage, LanguagePickerCard } from "./i18n/LanguageContext";
import { fetchCanadaGasolinePricePerLiter, estimateTripFuelCad, fuelCadPerKm } from "./fuelPricing";
import {
  resolveDriverLitersPer100km, heuristicLitersPer100km,
  fetchEpaYearOptions, fetchEpaMakeOptions, fetchEpaModelOptions, fetchEpaTrimOptions,
} from "./vehicleFuel";

const DAYS=["星期日","星期一","星期二","星期三","星期四","星期五","星期六"];

/** 原型：暫停車主檔案上載；改 false 即可恢復自拍＋駕照上載（正式上線前記得開返） */
const DRIVER_UPLOADS_DISABLED = true;

const P_GENDER_LABEL={male:"男",female:"女",other:"其他"};
const D_GENDER_OPTIONS=[
  {value:"male",label:"男",emoji:"👨"},
  {value:"female",label:"女",emoji:"👩"},
  {value:"other",label:"其他",emoji:"🧑"},
];
const dGenderLabel=g=>D_GENDER_OPTIONS.find(o=>o.value===g)?.label||"";

/** 用系統預設開 Google Maps 駕車導航去指定地址（通常會用你而家位置做起點） */
const openGoogleMapsNav=addr=>{
  const q=(addr||"").toString().trim();
  if(!q)return;
  const enc=encodeURIComponent(q);
  window.open(
    `https://www.google.com/maps/dir/?api=1&destination=${enc}&travelmode=driving`,
    "_blank",
    "noopener,noreferrer"
  );
};

const Driver=({onBack,bottomNavLift=0})=>{
  const { t } = useLanguage();
  const contentPadBottom=70+bottomNavLift;
  const bottomNavItems = useMemo(() => [
    { key: "orders", Icon: CarFront, label: t("nav.ride") },
    { key: "history", Icon: ScrollText, label: t("nav.history") },
    { key: "profile", Icon: UserRound, label: t("nav.profile") },
    { key: "settings", Icon: Settings, label: t("nav.settings") },
  ], [t]);

  // ── AUTH STATE ──────────────────────────────────────────────────────
  const [step,setStep]=useState("login");
  const [loginMethod,setLoginMethod]=useState("email");
  const [dphone,setDphone]=useState("");
  const [dsmsCooldown,setDsmsCooldown]=useState(0);
  const [dconfirmResult,setDconfirmResult]=useState(null);
  const [email,setEmail]=useState("");
  const [password,setPassword]=useState("");
  const [showPw,setShowPw]=useState(false);
  const [code,setCode]=useState("");

  // ── PROFILE STATE ──────────────────────────────────────────────────
  const [dname,setDname]=useState("");
  const [plate,setPlate]=useState("");
  const [langs,setLangs]=useState([]);
  const [vtype,setVtype]=useState("轎車");
  const [bio,setBio]=useState("");
  const [selfie,setSelfie]=useState(null);
  const [selfiePreview,setSelfiePreview]=useState(null);
  const [acceptChild,setAcceptChild]=useState(false);
  const [childSeats,setChildSeats]=useState({rear:0,forward:0,booster:0});
  const [acceptPet,setAcceptPet]=useState(false);
  const [petTypes,setPetTypes]=useState([]);
  const [schedule,setSchedule]=useState([]);
  const [avgRating,setAvgRating]=useState(0);
  const [ratingCount,setRatingCount]=useState(0);
  // Social profile
  const [occupation,setOccupation]=useState("");
  const [expertise,setExpertise]=useState([]);
  const [hobbies,setHobbies]=useState([]);
  const [gender,setGender]=useState("");
  const [vehicleYear,setVehicleYear]=useState("");
  const [vehicleMake,setVehicleMake]=useState("");
  const [vehicleModel,setVehicleModel]=useState("");
  const [vehicleTrim,setVehicleTrim]=useState("");
  const [vehicleEpaId,setVehicleEpaId]=useState("");
  const [vehiclePickerManual,setVehiclePickerManual]=useState(false);
  const [epaYearRows,setEpaYearRows]=useState([]);
  const [epaMakeRows,setEpaMakeRows]=useState([]);
  const [epaModelRows,setEpaModelRows]=useState([]);
  const [epaTrimRows,setEpaTrimRows]=useState([]);
  const [epaLoad,setEpaLoad]=useState({years:false,makes:false,models:false,trims:false});
  const [epaMenuErr,setEpaMenuErr]=useState("");
  const [manualLiters100,setManualLiters100]=useState("");
  const [litersPer100km,setLitersPer100km]=useState(null);
  const [fuelEconomySource,setFuelEconomySource]=useState("");
  const [fuelLookupBusy,setFuelLookupBusy]=useState(false);

  // ── DOCS STATE ─────────────────────────────────────────────────────
  const [licenseFrontFile,setLicenseFrontFile]=useState(null);
  const [licenseFrontPreview,setLicenseFrontPreview]=useState(null);
  const [licenseBackFile,setLicenseBackFile]=useState(null);
  const [licenseBackPreview,setLicenseBackPreview]=useState(null);

  // ── ORDERS STATE ───────────────────────────────────────────────────
  const [orders,setOrders]=useState([]);
  const [driverCurrentLoc,setDriverCurrentLoc]=useState(null);
  const [orderTab,setOrderTab]=useState("now");
  const [grabbed,setGrabbed]=useState(null);
  const [driverUpcoming,setDriverUpcoming]=useState(null);
  const [peerModal,setPeerModal]=useState(null);
  const [arrived,setArrived]=useState(false);
  const [online,setOnline]=useState(true);
  const [recDone,setRecDone]=useState(false);

  // ── HISTORY STATE ──────────────────────────────────────────────────
  const [history,setHistory]=useState([]);
  const [historyLoading,setHistoryLoading]=useState(false);

  // ── SETTINGS STATE ─────────────────────────────────────────────────
  const [settingsSub,setSettingsSub]=useState(null);
  const [uploading,setUploading]=useState(false);

  // ── PUBLISHED FIXED ROUTES ────────────────────────────────────────
  const [myDriverRoutes,setMyDriverRoutes]=useState([]);
  const [driverRouteBookings,setDriverRouteBookings]=useState([]);
  const [newRouteWeekdays,setNewRouteWeekdays]=useState([]);
  const [newRoutePickup,setNewRoutePickup]=useState("");
  const [newRouteDest,setNewRouteDest]=useState("");
  const [newRouteTime,setNewRouteTime]=useState("07:30");
  const [newRouteSeats,setNewRouteSeats]=useState(4);
  const [newRouteKind,setNewRouteKind]=useState("weekly");
  const [newRouteRunDate,setNewRouteRunDate]=useState("");
  const [newRouteChargeCad,setNewRouteChargeCad]=useState("");
  const [newRoutePricingKm,setNewRoutePricingKm]=useState(null);
  const [newRouteRefBand,setNewRouteRefBand]=useState(null);
  const [liveGasCadPerL,setLiveGasCadPerL]=useState(null);
  const [myRoutesGasFetched,setMyRoutesGasFetched]=useState(false);
  /** @type {{ [routeId:string]: { loading?:boolean, kmNum?:number|null, distText?:string } }} */
  const [driverRouteTripMeta,setDriverRouteTripMeta]=useState({});

  const myRoutesDistKey=useMemo(
    ()=>JSON.stringify((myDriverRoutes||[]).map(x=>({
      id:x.id,pickup:x.pickup||"",dest:x.dest||"",
      routeKind:x.routeKind||"weekly",runDate:x.runDate||"",
    }))),
    [myDriverRoutes]
  );

  // ── ETA / NAV / CHAT ───────────────────────────────────────────────
  const [etaData,setEtaData]=useState(null);
  const [navTab,setNavTab]=useState("orders");
  const [chatMessages,setChatMessages]=useState([]);
  const [chatUnreadCount,setChatUnreadCount]=useState(0);
  const [routeChatBooking,setRouteChatBooking]=useState(null);
  const [routeChatMessages,setRouteChatMessages]=useState([]);
  const [editPhone,setEditPhone]=useState("");

  const fileRef=useRef(null);
  const watchIdRef=useRef(null);
  const waitingChatRef=useRef(null);
  const chatSeenIdsRef=useRef(new Set());
  const chatReadyRef=useRef(false);

  const applyPassengerDataToForm=(p,user)=>{
    if(user?.email)setEmail(user.email);
    setDname(p.name||"");
    const ph=String(p.phone||"").replace(/\D/g,"");
    if(ph.length>=10)setDphone(ph.slice(-10));
    else if(ph.length>0)setDphone(ph);
    setGender(p.gender||"");
  };

  // ── AUTO LOGIN ON MOUNT（與乘客共用同一 Firebase 帳戶）────────────────
  useEffect(()=>{
    let initialCheck=true;
    const unsub=auth.onAuthStateChanged(async user=>{
      if(!initialCheck)return;
      initialCheck=false;
      if(!user)return;
      try{
        const dSnap=await getDoc(doc(db,"drivers",user.uid));
        if(dSnap.exists()){
          const data=dSnap.data();
          if(data.status==="approved"){
            loadDriverData(data);
            requestNotificationPermission(user.uid,"driver");
            const hasActive=await restoreActiveOrder(user.uid);
            if(!hasActive)setStep("orders");
            return;
          }
          if(data.status==="pending"){setStep("pending");return;}
          if(data.status==="rejected"){
            const pSnap=await getDoc(doc(db,"passengers",user.uid));
            if(pSnap.exists())applyPassengerDataToForm(pSnap.data(),user);
            setStep("register");
            return;
          }
          loadDriverData(data);
          requestNotificationPermission(user.uid,"driver");
          const hasActive=await restoreActiveOrder(user.uid);
          if(!hasActive)setStep("orders");
          return;
        }
        const pSnap=await getDoc(doc(db,"passengers",user.uid));
        if(!pSnap.exists()){
          await auth.signOut();
          return;
        }
        applyPassengerDataToForm(pSnap.data(),user);
        setStep("register");
      }catch(e){console.error(e);}
    });
    return()=>unsub();
  },[]);

  // ── SMS COOLDOWN ───────────────────────────────────────────────────
  useEffect(()=>{
    if(dsmsCooldown<=0)return;
    const t=setInterval(()=>setDsmsCooldown(c=>c-1),1000);
    return()=>clearInterval(t);
  },[dsmsCooldown]);

  // ── ONLINE LOCATION HEARTBEAT ─────────────────────────────────────
  useEffect(()=>{
    if(step!=="orders"||!online||!auth.currentUser?.uid||!navigator.geolocation)return;
    const uid=auth.currentUser.uid;
    const wid=navigator.geolocation.watchPosition(
      pos=>{
        const loc={lat:pos.coords.latitude,lng:pos.coords.longitude};
        setDriverCurrentLoc(loc);
        updateDoc(doc(db,"drivers",uid),{
          currentLocation:loc,
          locationUpdatedAt:serverTimestamp(),
          online:true,
        }).catch(()=>{});
      },
      ()=>{},
      {enableHighAccuracy:true,maximumAge:10000,timeout:10000}
    );
    return()=>navigator.geolocation.clearWatch(wid);
  },[step,online,auth.currentUser?.uid]);

  useEffect(()=>{
    if(!auth.currentUser?.uid)return;
    updateDoc(doc(db,"drivers",auth.currentUser.uid),{online:!!online}).catch(()=>{});
  },[online,auth.currentUser?.uid]);

  useEffect(()=>{
    const uid=auth.currentUser?.uid;
    if(!uid)return;
    const skip=["login","register","docs","pending","driverSms"];
    if(skip.includes(step))return;
    const q=query(collection(db,"driver_routes"),where("driverId","==",uid));
    return onSnapshot(q,snap=>{
      const rows=snap.docs.map(d=>({id:d.id,...d.data()}));
      rows.sort((a,b)=>(a.departTime||"").localeCompare(b.departTime||""));
      setMyDriverRoutes(rows);
    });
  },[auth.currentUser?.uid,step]);

  useEffect(()=>{
    const uid=auth.currentUser?.uid;
    if(!uid)return;
    const skip=["login","register","docs","pending","driverSms"];
    if(skip.includes(step))return;
    const q=query(collection(db,"route_bookings"),where("driverId","==",uid),orderBy("runDate","desc"),limit(80));
    return onSnapshot(q,snap=>setDriverRouteBookings(snap.docs.map(d=>({id:d.id,...d.data()}))),e=>{
      console.error("[Driver] route_bookings orderBy failed, fallback",e);
      const qFb=query(collection(db,"route_bookings"),where("driverId","==",uid),limit(120));
      getDocs(qFb).then(snap2=>{
        const rows=snap2.docs.map(d=>({id:d.id,...d.data()}));
        rows.sort((a,b)=>(b.runDate||"").localeCompare(a.runDate||""));
        setDriverRouteBookings(rows.slice(0,80));
      }).catch(()=>setDriverRouteBookings([]));
    });
  },[auth.currentUser?.uid,step]);

  const toggleNewRouteWeekday=d=>{
    setNewRouteWeekdays(w=>w.includes(d)?w.filter(x=>x!==d):[...w,d].sort((a,b)=>a-b));
  };

  const saveNewDriverRoute=async()=>{
    const uid=auth.currentUser?.uid;
    if(!uid)return;
    if(!newRoutePickup.trim()||!newRouteDest.trim()){alert("請填上車地點同目的地");return;}
    if(newRouteKind==="weekly"&&newRouteWeekdays.length===0){alert("請揀至少一個行車日子（每週重複）");return;}
    if(newRouteKind==="oneOff"){
      if(!newRouteRunDate){alert("請揀指定出發日期");return;}
      if(newRouteRunDate<todayIsoLocal()){alert("指定日期必須係今日或以後");return;}
    }
    try{
      const ds=await getDoc(doc(db,"drivers",uid));
      if(ds.data()?.status!=="approved"){alert("帳戶未獲批准，無法刊登路線");return;}
      const charge=parseFloat(String(newRouteChargeCad).replace(",","."));
      if(Number.isNaN(charge)||charge<0){alert("請填寫每位收費（加元，數字 ≥ 0；可填 0 表示免費或另議）");return;}
      const ref=newRouteRefBand;
      const base={
        driverId:uid,driverName:dname,driverPlate:plate,driverVtype:vtype,
        pickup:newRoutePickup.trim(),
        dest:newRouteDest.trim(),
        departTime:newRouteTime||"08:00",
        totalSeats:Math.min(8,Math.max(1,parseInt(String(newRouteSeats),10)||4)),
        active:true,
        createdAt:serverTimestamp(),
        chargePerPassengerCad:Math.round(charge*100)/100,
        referenceCarpoolMidCad:ref?.mid??null,
        referenceCarpoolLowCad:ref?.low??null,
        referenceCarpoolHighCad:ref?.high??null,
        routeDistanceKm:newRoutePricingKm!=null&&Number.isFinite(newRoutePricingKm)?Math.round(newRoutePricingKm*10)/10:null,
      };
      if(newRouteKind==="oneOff"){
        await addDoc(collection(db,"driver_routes"),{
          ...base,routeKind:"oneOff",runDate:newRouteRunDate,weekdays:[],
        });
      }else{
        await addDoc(collection(db,"driver_routes"),{
          ...base,routeKind:"weekly",weekdays:newRouteWeekdays,runDate:null,
        });
      }
      setNewRoutePickup("");setNewRouteDest("");setNewRouteWeekdays([]);setNewRouteRunDate("");
      setNewRouteChargeCad("");setNewRoutePricingKm(null);setNewRouteRefBand(null);
      alert("✅ 路線已刊登，乘客可喺「固定路線預約」預留座位。");
    }catch(e){alert("刊登失敗："+e.message);}
  };

  const flipDriverRouteActive=async r=>{
    try{
      await updateDoc(doc(db,"driver_routes",r.id),{active:!r.active});
    }catch(e){alert(e.message);}
  };

  // ── ORDERS LISTENER（waiting + 乘客邀請 awaiting_driver）──────────────
  useEffect(()=>{
    if(step!=="orders")return;
    const uid=auth.currentUser?.uid;
    const mapRow=(d,st)=>{
      const od=d.data();
      return{
        id:d.id,orderStatus:st,
        passengerId:od.passengerId||null,
        p:od.passengerName,pAvatar:od.passengerAvatar||null,
        pOccupation:od.passengerOccupation||"",pExpertise:od.passengerExpertise||[],pHobbies:od.passengerHobbies||[],
        pGender:od.passengerGender||"",
        pu:od.pickup,dt:od.dest,stops:od.stops||[],
        fare:od.fare,fareNum:od.fareNum||0,
        dist:od.distKm?`${od.distKm}km`:"附近",distKmNum:parseFloat(od.distKm)||0,durMin:od.durMin||0,
        pax:od.pax,luggage:od.luggage,
        hasChild:od.hasChild,hasPet:od.hasPet,
          isScheduled:!!od.isScheduled,scheduledTime:od.scheduledTime||null,
          createdAt:od.createdAt,phone:od.phone||"",
      };
    };
    let waitingRows=[],inviteRows=[];
    const flush=()=>{
      const byId=new Map();
      inviteRows.forEach(o=>byId.set(o.id,o));
      waitingRows.forEach(o=>byId.set(o.id,o));
      const merged=[...byId.values()].sort((a,b)=>(b.createdAt?.seconds||0)-(a.createdAt?.seconds||0));
      setOrders(merged);
    };
    const q1=query(collection(db,"orders"),where("status","==","waiting"));
    const unsub1=onSnapshot(q1,(snap)=>{
      waitingRows=snap.docs.map(d=>mapRow(d,"waiting"));
      flush();
    });
    let unsub2=()=>{};
    if(uid){
      const q2=query(collection(db,"orders"),where("status","==","awaiting_driver"),where("driverId","==",uid));
      unsub2=onSnapshot(q2,(snap)=>{
        inviteRows=snap.docs.map(d=>mapRow(d,"awaiting_driver"));
        flush();
      });
    }
    return()=>{unsub1();unsub2();};
  },[step,auth.currentUser?.uid]);

  // ── ORDER CARDS: distance from me to pickup ───────────────────────
  useEffect(()=>{
    if(step!=="orders"||!driverCurrentLoc||orders.length===0)return;
    let cancelled=false;
    const run=async()=>{
      const rows=await Promise.all(orders.map(async o=>{
        if(!o?.pu)return null;
        try{
          const eta=await calcDistanceETA(`${driverCurrentLoc.lat},${driverCurrentLoc.lng}`,o.pu);
          if(!eta?.kmText)return null;
          return {id:o.id,pickupDistText:eta.kmText,pickupDurText:eta.durText};
        }catch(e){return null;}
      }));
      if(cancelled)return;
      const m=new Map(rows.filter(Boolean).map(r=>[r.id,r]));
      setOrders(prev=>prev.map(o=>m.has(o.id)?{...o,...m.get(o.id)}:o));
    };
    run();
    return()=>{cancelled=true;};
  },[step,driverCurrentLoc?.lat,driverCurrentLoc?.lng,orders.map(o=>o.id).join("|")]);

  // ── 預約待出發：列表頂提示 ───────────────────────────────────────────
  useEffect(()=>{
    if(step!=="orders"||!auth.currentUser?.uid)return;
    const q=query(collection(db,"orders"),where("driverId","==",auth.currentUser.uid),where("status","==","scheduled_pending"),limit(1));
    const unsub=onSnapshot(q,snap=>{
      if(snap.empty){setDriverUpcoming(null);return;}
      const doc0=snap.docs[0];
      setDriverUpcoming({id:doc0.id,...doc0.data()});
    });
    return()=>unsub();
  },[step,auth.currentUser?.uid]);

  // ── 進行中訂單：同步狀態 + 到點升級 grabbed ─────────────────────────
  useEffect(()=>{
    if(step!=="grabbed"||!grabbed?.id)return;
    const id=grabbed.id;
    const unsub=onSnapshot(doc(db,"orders",id),snap=>{
      const d=snap.data();
      if(!d)return;
      if(shouldPromoteScheduledPending(d)){
        updateDoc(doc(db,"orders",id),{status:"grabbed",grabbedAt:serverTimestamp()}).catch(()=>{});
      }
      setGrabbed(g=>({
        ...g,_orderStatus:d.status,scheduledTime:d.scheduledTime,isScheduled:d.isScheduled,
      }));
    });
    return()=>unsub();
  },[step,grabbed?.id]);

  // ── GPS TRACKING WHEN GRABBED ──────────────────────────────────────
  useEffect(()=>{
    if(step==="grabbed"&&grabbed&&grabbed._orderStatus!=="scheduled_pending"&&navigator.geolocation){
      watchIdRef.current=navigator.geolocation.watchPosition(
        (pos)=>{
          const loc={lat:pos.coords.latitude,lng:pos.coords.longitude};
          updateDoc(doc(db,"orders",grabbed.id),{driverLocation:loc}).catch(()=>{});
        },
        (err)=>{console.log("GPS error:",err);},
        {enableHighAccuracy:true,maximumAge:5000,timeout:10000}
      );
      return()=>{
        if(watchIdRef.current!==null){
          navigator.geolocation.clearWatch(watchIdRef.current);
          watchIdRef.current=null;
        }
      };
    }
  },[step,grabbed?.id,grabbed?._orderStatus]);

  // ── CHAT LISTENER ──────────────────────────────────────────────────
  useEffect(()=>{
    if((step==="grabbed"||step==="pending_passenger")&&grabbed){
      const q=query(collection(db,"chats",grabbed.id,"messages"),orderBy("createdAt","asc"));
      const unsub=onSnapshot(q,(snap)=>{
        const rows=snap.docs.map(d=>{
          const data=d.data();
          return{
            id:d.id,
            senderId:data.senderId,
            senderName:data.senderName,
            text:data.text,
            time:data.createdAt?formatTime(data.createdAt):"",
          };
        });
        setChatMessages(rows);

        if(!chatReadyRef.current){
          rows.forEach(m=>chatSeenIdsRef.current.add(m.id));
          chatReadyRef.current=true;
          return;
        }

        const myUid=auth.currentUser?.uid;
        let incoming=0;
        rows.forEach(m=>{
          if(chatSeenIdsRef.current.has(m.id))return;
          chatSeenIdsRef.current.add(m.id);
          if(step==="pending_passenger"&&m.senderId&&m.senderId!==myUid)incoming+=1;
        });
        if(incoming>0)setChatUnreadCount(c=>c+incoming);
      });
      return()=>unsub();
    }
  },[step,grabbed?.id]);

  useEffect(()=>{
    if(!routeChatBooking?.id){
      setRouteChatMessages([]);
      return;
    }
    const q=query(collection(db,"route_chats",routeChatBooking.id,"messages"),orderBy("createdAt","asc"));
    const unsub=onSnapshot(q,(snap)=>{
      setRouteChatMessages(snap.docs.map(d=>{
        const data=d.data();
        return{
          id:d.id,
          senderId:data.senderId,
          senderName:data.senderName,
          text:data.text,
          time:data.createdAt?formatTime(data.createdAt):"",
        };
      }));
    });
    return()=>unsub();
  },[routeChatBooking?.id]);

  useEffect(()=>{
    chatSeenIdsRef.current=new Set();
    chatReadyRef.current=false;
    setChatUnreadCount(0);
  },[grabbed?.id]);

  useEffect(()=>{
    if(step!=="pending_passenger")setChatUnreadCount(0);
  },[step]);

  // ── 等乘客確認接單 ─────────────────────────────────────────────────
  useEffect(()=>{
    if(step!=="pending_passenger"||!grabbed?.id)return;
    const unsub=onSnapshot(doc(db,"orders",grabbed.id),snap=>{
      const d=snap.data();
      if(!d)return;
      if(d.status==="grabbed"||d.status==="scheduled_pending"){
        setGrabbed(g=>({
          ...g,id:grabbed.id,passengerId:d.passengerId||g?.passengerId||null,p:d.passengerName,pu:d.pickup,dt:d.dest,
          fare:d.fare,pax:d.pax,phone:d.phone||"",stops:d.stops||[],
          pAvatar:d.passengerAvatar||null,pGender:d.passengerGender||"",
          _orderStatus:d.status,scheduledTime:d.scheduledTime,isScheduled:d.isScheduled,
        }));
        if(d.pickup&&d.status==="grabbed"){
          calcDistanceETA("current location",d.pickup).then(eta=>{
            if(eta)setEtaData(eta);
          }).catch(()=>{});
        }
        setStep("grabbed");
      }else if(d.status==="waiting"){
        alert("乘客未接受你嘅接單");
        setGrabbed(null);
        setEtaData(null);
        setStep("orders");
      }else if(d.status==="cancelled"){
        alert("訂單已取消");
        setGrabbed(null);
        setEtaData(null);
        setStep("orders");
      }
    });
    return()=>unsub();
  },[step,grabbed?.id]);

  const sendChatMessage=(text)=>{
    if(!grabbed||!auth.currentUser)return;
    setChatUnreadCount(0);
    addDoc(collection(db,"chats",grabbed.id,"messages"),{
      senderId:auth.currentUser.uid,
      senderName:dname,
      senderRole:"driver",
      text,
      createdAt:serverTimestamp(),
    }).catch(e=>console.error("Chat send failed:",e));
  };

  const sendRouteChatMessage=(text)=>{
    if(!routeChatBooking?.id||!auth.currentUser)return;
    addDoc(collection(db,"route_chats",routeChatBooking.id,"messages"),{
      senderId:auth.currentUser.uid,
      senderName:dname||"車主",
      senderRole:"driver",
      text,
      createdAt:serverTimestamp(),
    }).catch(e=>console.error("Route chat send failed:",e));
  };

  const focusWaitingChat=()=>{
    setChatUnreadCount(0);
    waitingChatRef.current?.scrollIntoView({behavior:"smooth",block:"start"});
  };

  // ── GRAB ORDER ─────────────────────────────────────────────────────
  const grab=async o=>{
    if(o.orderStatus&&o.orderStatus!=="waiting")return;
    try{
      const vy=parseInt(vehicleYear,10)||null;
      const l100=vtype==="電動車"?null:(litersPer100km ?? (vy?heuristicLitersPer100km(vtype,vy):heuristicLitersPer100km(vtype,2018)));
      await updateDoc(doc(db,"orders",o.id),{
        status:"awaiting_passenger",
        driverId:auth.currentUser?.uid||null,
        driverName:dname,driverPlate:plate,
        driverPhone:dphone||email||"",
        driverLang:langs,driverVtype:vtype,
        driverAvgRating:avgRating,
        driverOccupation:occupation||"",
        driverExpertise:expertise||[],
        driverHobbies:hobbies||[],
        driverSelfieURL:selfiePreview||null,
        driverVehicleYear:vy,
        driverVehicleMake:vehicleMake.trim()||null,
        driverVehicleModel:combinedDriverVehicleModel()||null,
        driverLitersPer100km:l100,
        driverAcceptedAt:serverTimestamp(),
      });
      setGrabbed({...o,passengerId:o.passengerId||null});
      if(o.pu){
        calcDistanceETA("current location",o.pu).then(eta=>{
          if(eta)setEtaData(eta);
        }).catch(()=>{});
      }
      setStep("pending_passenger");
    }catch(err){alert("接受Carpool失敗："+err.message);}
  };

  const acceptPassengerInvite=async o=>{
    try{
      const ref=doc(db,"orders",o.id);
      const snap=await getDoc(ref);
      const d=snap.data();
      if(!d)return;
      const next=getMatchStatusAfterConfirm(d);
      const patch={status:next,matchConfirmedAt:serverTimestamp()};
      if(next==="grabbed"){
        patch.grabbedAt=serverTimestamp();
        const vy=parseInt(vehicleYear,10)||null;
        const l100=vtype==="電動車"?null:(litersPer100km ?? (vy?heuristicLitersPer100km(vtype,vy):heuristicLitersPer100km(vtype,2018)));
        patch.driverVehicleYear=vy;
        patch.driverVehicleMake=vehicleMake.trim()||null;
        patch.driverVehicleModel=combinedDriverVehicleModel()||null;
        patch.driverLitersPer100km=l100;
      }
      await updateDoc(ref,patch);
      setGrabbed({
        ...o,passengerId:d.passengerId||o.passengerId||null,_orderStatus:next,scheduledTime:d.scheduledTime,isScheduled:d.isScheduled,
      });
      if(o.pu&&next==="grabbed"){
        calcDistanceETA("current location",o.pu).then(eta=>{
          if(eta)setEtaData(eta);
        }).catch(()=>{});
      }
      setStep("grabbed");
    }catch(err){alert("確認失敗："+err.message);}
  };

  const declinePassengerInvite=async o=>{
    if(!window.confirm("婉拒呢個邀請？乘客可以揀其他車主。"))return;
    try{
      await updateDoc(doc(db,"orders",o.id),{status:"waiting",...ORDER_CLEAR_DRIVER});
    }catch(err){alert("操作失敗");}
  };

  const withdrawDriverOffer=async()=>{
    if(!grabbed?.id)return;
    if(!window.confirm("撤回接單？乘客要重新配對。"))return;
    try{
      await updateDoc(doc(db,"orders",grabbed.id),{status:"waiting",...ORDER_CLEAR_DRIVER});
      setGrabbed(null);
      setEtaData(null);
      setStep("orders");
    }catch(e){alert("撤回失敗");}
  };

  // ── RESTORE ACTIVE ORDER ───────────────────────────────────────────
  const restoreActiveOrder=async(uid)=>{
    try{
      const qG=query(collection(db,"orders"),where("driverId","==",uid),where("status","==","grabbed"),limit(1));
      const qSP=query(collection(db,"orders"),where("driverId","==",uid),where("status","==","scheduled_pending"),limit(1));
      const qP=query(collection(db,"orders"),where("driverId","==",uid),where("status","==","awaiting_passenger"),limit(1));
      const [snapG,snapSP,snapP]=await Promise.all([getDocs(qG),getDocs(qSP),getDocs(qP)]);
      const build=o=>{
        const d=o.data();
        return{
          id:o.id,passengerId:d.passengerId||null,p:d.passengerName,pu:d.pickup,dt:d.dest,
          fare:d.fare,pax:d.pax,
          phone:d.phone||"",stops:d.stops||[],pAvatar:d.passengerAvatar||null,
          pGender:d.passengerGender||"",
          orderStatus:d.status,
          _orderStatus:d.status,
          scheduledTime:d.scheduledTime,
          isScheduled:d.isScheduled,
        };
      };
      if(!snapG.empty){
        setGrabbed(build(snapG.docs[0]));
        setStep("grabbed");
        return true;
      }
      if(!snapSP.empty){
        setGrabbed(build(snapSP.docs[0]));
        setStep("grabbed");
        return true;
      }
      if(!snapP.empty){
        setGrabbed(build(snapP.docs[0]));
        setStep("pending_passenger");
        return true;
      }
    }catch(e){console.error(e);}
    return false;
  };

  // ── LOAD HISTORY ───────────────────────────────────────────────────
  const loadHistory=async()=>{
    if(!auth.currentUser)return;
    setHistoryLoading(true);
    try{
      const q=query(collection(db,"orders"),where("driverId","==",auth.currentUser.uid),orderBy("createdAt","desc"),limit(50));
      const snap=await getDocs(q);
      setHistory(snap.docs.map(d=>({id:d.id,...d.data()})));
    }catch(e){console.error(e);}
    setHistoryLoading(false);
  };

  const openDriverHistoryOrder=async o=>{
    if(!o?.id)return;
    const reopen=["scheduled_pending","grabbed","awaiting_passenger"];
    if(!reopen.includes(o.status))return;
    try{
      const snap=await getDoc(doc(db,"orders",o.id));
      const d=snap.data();
      if(!d)return;
      const g={
        id:o.id,passengerId:d.passengerId||null,p:d.passengerName,pu:d.pickup,dt:d.dest,
        fare:d.fare,pax:d.pax,phone:d.phone||"",stops:d.stops||[],
        pAvatar:d.passengerAvatar||null,pGender:d.passengerGender||"",
        _orderStatus:d.status,scheduledTime:d.scheduledTime,isScheduled:d.isScheduled,
      };
      if(d.status==="scheduled_pending"||d.status==="grabbed"){
        setGrabbed(g);
        if(d.pickup&&d.status==="grabbed"){
          calcDistanceETA("current location",d.pickup).then(eta=>{
            if(eta)setEtaData(eta);
          }).catch(()=>{});
        }else setEtaData(null);
        setArrived(false);
        setStep("grabbed");
      }else if(d.status==="awaiting_passenger"){
        setGrabbed(g);
        setEtaData(null);
        setStep("pending_passenger");
      }
    }catch(e){console.error(e);}
  };

  // ── PROFILE HELPERS ────────────────────────────────────────────────
  const toggleLang=l=>setLangs(p=>p.includes(l)?p.filter(x=>x!==l):[...p,l]);
  const togglePetType=v=>setPetTypes(p=>p.includes(v)?p.filter(x=>x!==v):[...p,v]);
  const totalChildSeats=Object.values(childSeats).reduce((a,b)=>a+b,0);

  const handleSelfie=e=>{
    const file=e.target.files[0];if(!file)return;
    setSelfie(file);
    const reader=new FileReader();
    reader.onload=ev=>setSelfiePreview(ev.target.result);
    reader.readAsDataURL(file);
  };
  const handleLicenseFront=e=>{
    const file=e.target.files[0];if(!file)return;
    setLicenseFrontFile(file);
    const reader=new FileReader();
    reader.onload=ev=>setLicenseFrontPreview(ev.target.result);
    reader.readAsDataURL(file);
  };
  const handleLicenseBack=e=>{
    const file=e.target.files[0];if(!file)return;
    setLicenseBackFile(file);
    const reader=new FileReader();
    reader.onload=ev=>setLicenseBackPreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  const handleSubmit=async()=>{
    if(!auth.currentUser){alert("❌ 請先登入再提交");return;}

    if(DRIVER_UPLOADS_DISABLED){
      try{
        setUploading(true);
        const id=auth.currentUser.uid;
        const vy=parseInt(vehicleYear,10);
        const fuelRes=await resolveDriverLitersPer100km({
          vtype,vehicleYear:vy,vehicleMake,vehicleModel,manualLiters100,
          vehicleEpaId:vehiclePickerManual?"":vehicleEpaId,
        });
        await setDoc(doc(db,"drivers",id),{
          uid:id,name:dname,plate,langs,vehicleType:vtype,bio,
          phone:dphone||email||"",email:email||"",
          gender:gender||"",
          vehicleYear:vy||null,
          vehicleMake:vehicleMake.trim()||"",
          vehicleModel:vehicleModel.trim()||"",
          vehicleTrim:vehicleTrim.trim()||"",
          vehicleEpaId:vehiclePickerManual?"":(vehicleEpaId.trim()||null),
          vehiclePickerManual:!!vehiclePickerManual,
          litersPer100km:fuelRes.litersPer100km,
          fuelEconomySource:fuelRes.fuelEconomySource,
          acceptChild,childSeats,acceptPet,petTypes,
          occupation:occupation||"",expertise:expertise||[],hobbies:hobbies||[],
          schedule:[],
          licenseURL:null,
          licenseFrontURL:null,
          licenseBackURL:null,
          selfieURL:null,
          uploadsSkippedPrototype:true,
          totalRating:0,ratingCount:0,
          status:"pending",submittedAt:serverTimestamp(),fcmToken:null,
        });
        setUploading(false);
        try{
          await emailjs.send("service_23t0zgu","template_bgj35a9",{
            driver_name:dname,driver_plate:plate,driver_vtype:vtype,
            driver_langs:langs.join(", "),driver_bio:bio||"未填寫",
            submit_time:new Date().toLocaleString("zh-HK"),
          },"lwwmBiGa9G8vQEug1");
        }catch(e){console.log("emailjs send failed:",e);}
        setStep("pending");
      }catch(err){
        setUploading(false);
        console.error(err);
        alert("❌ 提交失敗，請重試："+(err?.message||"未知錯誤"));
      }
      return;
    }

    if(!licenseFrontFile||!licenseBackFile){alert("請上載駕照「正面」同「背面」");return;}
    if(!selfie){alert("請返回上一步，上載正面人像自拍（作個人頭像同審核）");return;}
    try{
      setUploading(true);
      const id=auth.currentUser.uid;
      const licenseFrontURL=await uploadFile(licenseFrontFile,`drivers/${id}/license_front`);
      const licenseBackURL=await uploadFile(licenseBackFile,`drivers/${id}/license_back`);
      const selfieURL=await uploadFile(selfie,`drivers/${id}/selfie`);

      const vy=parseInt(vehicleYear,10);
      const fuelRes=await resolveDriverLitersPer100km({
        vtype,vehicleYear:vy,vehicleMake,vehicleModel,manualLiters100,
        vehicleEpaId:vehiclePickerManual?"":vehicleEpaId,
      });
      await setDoc(doc(db,"drivers",id),{
        uid:id,name:dname,plate,langs,vehicleType:vtype,bio,
        phone:dphone||email||"",email:email||"",
        gender:gender||"",
        vehicleYear:vy||null,
        vehicleMake:vehicleMake.trim()||"",
        vehicleModel:vehicleModel.trim()||"",
        vehicleTrim:vehicleTrim.trim()||"",
        vehicleEpaId:vehiclePickerManual?"":(vehicleEpaId.trim()||null),
        vehiclePickerManual:!!vehiclePickerManual,
        litersPer100km:fuelRes.litersPer100km,
        fuelEconomySource:fuelRes.fuelEconomySource,
        acceptChild,childSeats,acceptPet,petTypes,
        occupation:occupation||"",expertise:expertise||[],hobbies:hobbies||[],
        schedule:[],
        licenseURL:licenseFrontURL,
        licenseFrontURL,
        licenseBackURL,
        selfieURL,
        totalRating:0,ratingCount:0,
        status:"pending",submittedAt:serverTimestamp(),fcmToken:null,
      });
      setUploading(false);

      try{
        await emailjs.send("service_23t0zgu","template_bgj35a9",{
          driver_name:dname,driver_plate:plate,driver_vtype:vtype,
          driver_langs:langs.join(", "),driver_bio:bio||"未填寫",
          submit_time:new Date().toLocaleString("zh-HK"),
        },"lwwmBiGa9G8vQEug1");
      }catch(e){console.log("emailjs send failed:",e);}

      setStep("pending");
    }catch(err){
      setUploading(false);
      console.error(err);
      alert("❌ 上傳失敗，請重試："+(err?.message||"未知錯誤"));
    }
  };

  const saveDriverProfile=async()=>{
    if(!auth.currentUser)return;
    try{
      const vy=parseInt(vehicleYear,10);
      const fuelRes=await resolveDriverLitersPer100km({
        vtype,vehicleYear:vy,vehicleMake,vehicleModel,manualLiters100,
        vehicleEpaId:vehiclePickerManual?"":vehicleEpaId,
      });
      setLitersPer100km(fuelRes.litersPer100km);
      setFuelEconomySource(fuelRes.fuelEconomySource);
      await updateDoc(doc(db,"drivers",auth.currentUser.uid),{
        bio,acceptChild,childSeats,acceptPet,petTypes,schedule,
        occupation:occupation||"",expertise:expertise||[],hobbies:hobbies||[],
        vehicleYear:vy||null,
        vehicleMake:vehicleMake.trim()||"",
        vehicleModel:vehicleModel.trim()||"",
        vehicleTrim:vehicleTrim.trim()||"",
        vehicleEpaId:vehiclePickerManual?"":(vehicleEpaId.trim()||null),
        vehiclePickerManual:!!vehiclePickerManual,
        litersPer100km:fuelRes.litersPer100km,
        fuelEconomySource:fuelRes.fuelEconomySource,
      });
      alert("✅ 已更新");
    }catch(e){alert("❌ 更新失敗");}
  };

  const saveDriverPhone=async()=>{
    if(!auth.currentUser||!editPhone.trim())return;
    try{
      await updateDoc(doc(db,"drivers",auth.currentUser.uid),{phone:editPhone.trim()});
      setDphone(editPhone.trim());
      alert("✅ 電話已更新");
    }catch(e){alert("❌ 更新失敗");}
  };

  const addScheduleSlot=()=>setSchedule(p=>[...p,{day:1,start:"08:00",end:"18:00"}]);
  const removeScheduleSlot=(idx)=>setSchedule(p=>p.filter((_,i)=>i!==idx));
  const updateScheduleSlot=(idx,field,val)=>setSchedule(p=>{const n=[...p];n[idx]={...n[idx],[field]:val};return n;});

  const completeTrip=async()=>{
    if(!grabbed)return;
    if(grabbed._orderStatus==="scheduled_pending"){
      alert("預約尚未到出發時間，唔可以完成行程。");
      return;
    }
    if(watchIdRef.current!==null){
      navigator.geolocation.clearWatch(watchIdRef.current);
      watchIdRef.current=null;
    }
    try{
      await updateDoc(doc(db,"orders",grabbed.id),{status:"completed",completedAt:serverTimestamp(),driverLocation:null});
    }catch(e){}
    setGrabbed(null);setArrived(false);setEtaData(null);setChatMessages([]);setStep("orders");
  };

  const back=()=>{
    if(settingsSub){setSettingsSub(null);return;}
    if(step==="login")onBack();
    else if(step==="driverSms")setStep("login");
    else if(step==="register"){
      auth.signOut();
      setStep("login");
    }
    else if(step==="docs")setStep("register");
    else if(step==="orders")onBack();
    else if(step==="pending_passenger"){withdrawDriverOffer();}
    else if(step==="grabbed"){
      if(grabbed?._orderStatus==="scheduled_pending"){setStep("orders");}
      else{setGrabbed(null);setArrived(false);setChatMessages([]);setStep("orders");}
    }
    else if(step==="history"){setStep("orders");}
    else if(step==="settings"){setStep("orders");}
    else if(step==="my_profile"){setStep("orders");}
    else setStep("orders");
  };

  const handleNavTab=(tab)=>{
    setSettingsSub(null);
    setNavTab(tab);
    if(tab==="orders")setStep("orders");
    else if(tab==="history"){loadHistory();setStep("history");}
    else if(tab==="profile")setStep("my_profile");
    else if(tab==="settings")setStep("settings");
  };

  const showBottomNav=["orders","history","my_profile","settings"].includes(step);

  const loadDriverData=(data)=>{
    setDname(data.name||"");setPlate(data.plate||"");setLangs(data.langs||[]);
    setVtype(data.vehicleType||"轎車");setBio(data.bio||"");
    setSelfiePreview(data.selfieURL||null);
    setAcceptChild(data.acceptChild||false);
    setChildSeats(data.childSeats||{rear:0,forward:0,booster:0});
    setAcceptPet(data.acceptPet||false);setPetTypes(data.petTypes||[]);
    setSchedule(data.schedule||[]);
    setAvgRating(data.ratingCount>0?(data.totalRating/data.ratingCount):0);
    setRatingCount(data.ratingCount||0);
    setDphone(data.phone||"");setEmail(data.email||"");
    setOccupation(data.occupation||"");
    setExpertise(data.expertise||[]);
    setHobbies(data.hobbies||[]);
    setGender(data.gender||"");
    setVehicleYear(data.vehicleYear!=null?String(data.vehicleYear):"");
    setVehicleMake(data.vehicleMake||"");
    setVehicleModel(data.vehicleModel||"");
    setVehicleTrim(data.vehicleTrim||"");
    setVehicleEpaId(data.vehicleEpaId!=null?String(data.vehicleEpaId):"");
    setVehiclePickerManual(data.vehiclePickerManual===true);
    setManualLiters100("");
    setLitersPer100km(data.litersPer100km!=null?data.litersPer100km:null);
    setFuelEconomySource(data.fuelEconomySource||"");
  };

  const STATIC_YEARS=useMemo(()=>{
    const a=[];
    for(let n=2030;n>=1990;n--)a.push(String(n));
    return a;
  },[]);

  const needEpaYearList=step==="register"||(step==="settings"&&settingsSub==="profile");

  useEffect(()=>{
    if(vehiclePickerManual||!needEpaYearList)return;
    if(epaYearRows.length>0)return;
    let cancelled=false;
    setEpaLoad(p=>({...p,years:true}));
    setEpaMenuErr("");
    fetchEpaYearOptions(1990,2030)
      .then((rows)=>{
        if(cancelled)return;
        setEpaYearRows(rows);
        if(!rows.length)setEpaMenuErr("年份列表空白，請改用手動輸入");
      })
      .catch(()=>{
        if(cancelled)return;
        setEpaYearRows([]);
        setEpaMenuErr("無法連接 EPA 選單，請改用手動輸入");
      })
      .finally(()=>{if(!cancelled)setEpaLoad(p=>({...p,years:false}));});
    return()=>{cancelled=true;};
  },[step,settingsSub,vehiclePickerManual,epaYearRows.length,needEpaYearList]);

  useEffect(()=>{
    if(vehiclePickerManual)return;
    const y=parseInt(vehicleYear,10);
    if(!y){setEpaMakeRows([]);return;}
    let cancelled=false;
    setEpaLoad(p=>({...p,makes:true}));
    fetchEpaMakeOptions(y)
      .then((r)=>{if(!cancelled)setEpaMakeRows(r||[]);})
      .catch(()=>{if(!cancelled)setEpaMakeRows([]);})
      .finally(()=>{if(!cancelled)setEpaLoad(p=>({...p,makes:false}));});
    return()=>{cancelled=true;};
  },[vehicleYear,vehiclePickerManual]);

  useEffect(()=>{
    if(vehiclePickerManual)return;
    const y=parseInt(vehicleYear,10);
    const mk=(vehicleMake||"").trim();
    if(!y||!mk){setEpaModelRows([]);return;}
    let cancelled=false;
    setEpaLoad(p=>({...p,models:true}));
    fetchEpaModelOptions(y,mk)
      .then((r)=>{if(!cancelled)setEpaModelRows(r||[]);})
      .catch(()=>{if(!cancelled)setEpaModelRows([]);})
      .finally(()=>{if(!cancelled)setEpaLoad(p=>({...p,models:false}));});
    return()=>{cancelled=true;};
  },[vehicleYear,vehicleMake,vehiclePickerManual]);

  useEffect(()=>{
    if(vehiclePickerManual)return;
    const y=parseInt(vehicleYear,10);
    const mk=(vehicleMake||"").trim();
    const md=(vehicleModel||"").trim();
    if(!y||!mk||!md){setEpaTrimRows([]);return;}
    let cancelled=false;
    setEpaLoad(p=>({...p,trims:true}));
    fetchEpaTrimOptions(y,mk,md)
      .then((r)=>{if(!cancelled)setEpaTrimRows(r||[]);})
      .catch(()=>{if(!cancelled)setEpaTrimRows([]);})
      .finally(()=>{if(!cancelled)setEpaLoad(p=>({...p,trims:false}));});
    return()=>{cancelled=true;};
  },[vehicleYear,vehicleMake,vehicleModel,vehiclePickerManual]);

  const combinedDriverVehicleModel=()=>{
    const m=(vehicleModel||"").trim();
    const tr=(vehicleTrim||"").trim();
    if(vehiclePickerManual)return tr?`${m} · ${tr}`:m;
    return [m,tr].filter(Boolean).join(" · ");
  };

  const vehicleStepOk=()=>{
    const y=parseInt(vehicleYear,10);
    if(!(y>=1990&&y<=2030))return false;
    if(vehiclePickerManual)return (vehicleMake||"").trim().length>=1&&(vehicleModel||"").trim().length>=1;
    return (vehicleMake||"").trim().length>=1&&(vehicleModel||"").trim().length>=1&&(vehicleEpaId||"").trim().length>=1;
  };

  const runFuelLookup=async()=>{
    setFuelLookupBusy(true);
    try{
      const r=await resolveDriverLitersPer100km({
        vtype,vehicleYear,vehicleMake,vehicleModel,manualLiters100,
        vehicleEpaId:vehiclePickerManual?"":vehicleEpaId,
      });
      setLitersPer100km(r.litersPer100km);
      setFuelEconomySource(r.fuelEconomySource);
    }finally{
      setFuelLookupBusy(false);
    }
  };

  useEffect(()=>{
    if(!["orders","grabbed","pending_passenger"].includes(step))return;
    let cancelled=false;
    fetchCanadaGasolinePricePerLiter()
      .then(r=>{if(!cancelled)setLiveGasCadPerL(r.cadPerLiter);})
      .catch(()=>{if(!cancelled)setLiveGasCadPerL(null);});
    return()=>{cancelled=true;};
  },[step]);

  useEffect(()=>{
    if(step!=="settings"||settingsSub!=="my_routes"){
      setMyRoutesGasFetched(false);
      return;
    }
    let cancelled=false;
    setMyRoutesGasFetched(false);
    fetchCanadaGasolinePricePerLiter()
      .then(r=>{if(!cancelled)setLiveGasCadPerL(r.cadPerLiter);})
      .catch(()=>{if(!cancelled)setLiveGasCadPerL(null);})
      .finally(()=>{if(!cancelled)setMyRoutesGasFetched(true);});
    return()=>{cancelled=true;};
  },[step,settingsSub]);

  useEffect(()=>{
    if(step!=="settings"||settingsSub!=="my_routes"){
      setDriverRouteTripMeta({});
      return;
    }
    const routes=myDriverRoutes||[];
    if(!routes.length){
      setDriverRouteTripMeta({});
      return;
    }
    let cancelled=false;
    const ids=new Set(routes.map(x=>x.id));
    const nextInit={};
    routes.forEach(x=>{
      if((x.pickup||"").trim()&&(x.dest||"").trim())nextInit[x.id]={loading:true,kmNum:null,distText:""};
    });
    setDriverRouteTripMeta(nextInit);
    (async()=>{
      for(const rr of routes){
        if(cancelled)return;
        const pu=(rr.pickup||"").trim();
        const dt=(rr.dest||"").trim();
        if(!pu||!dt)continue;
        const eta=await calcDistanceETA(pu,dt);
        if(cancelled)return;
        const raw=eta?parseFloat(eta.distKm):NaN;
        setDriverRouteTripMeta(p=>({
          ...p,
          [rr.id]:{
            loading:false,
            kmNum:Number.isFinite(raw)&&raw>0?raw:null,
            distText:(eta&&eta.distText)||"",
          },
        }));
      }
      if(cancelled)return;
      setDriverRouteTripMeta(p=>{
        const n={...p};
        Object.keys(n).forEach(k=>{if(!ids.has(k))delete n[k];});
        return n;
      });
    })();
    return()=>{cancelled=true;};
  },[step,settingsSub,myRoutesDistKey]);

  useEffect(()=>{
    if(step!=="settings"||settingsSub!=="my_routes"){
      setNewRoutePricingKm(null);
      setNewRouteRefBand(null);
      return;
    }
    const pu=newRoutePickup.trim();
    const dt=newRouteDest.trim();
    if(!pu||!dt){
      setNewRoutePricingKm(null);
      setNewRouteRefBand(null);
      return;
    }
    let cancelled=false;
    const timer=setTimeout(()=>{
      calcDistanceETA(pu,dt).then(eta=>{
        if(cancelled||!eta)return;
        const km=parseFloat(eta.distKm);
        if(!Number.isFinite(km)||km<=0){
          setNewRoutePricingKm(null);
          setNewRouteRefBand(null);
          return;
        }
        setNewRoutePricingKm(km);
        setNewRouteRefBand(torontoCarpoolReferenceBand(km));
      }).catch(()=>{
        if(!cancelled){
          setNewRoutePricingKm(null);
          setNewRouteRefBand(null);
        }
      });
    },480);
    return()=>{clearTimeout(timer);cancelled=true;};
  },[step,settingsSub,newRoutePickup,newRouteDest]);

  if(routeChatBooking){
    return(
      <div style={{minHeight:"100vh",background:BG,paddingBottom:bottomNavLift}}>
        <Bar title={`固定路線聊天 · ${routeChatBooking.peerName||"乘客"}`} onBack={()=>setRouteChatBooking(null)}/>
        <Wrap>
          <Card style={{background:"#F0FDFA",border:`1px solid ${TEAL}33`}}>
            <p style={{color:SUB,fontSize:14,margin:0,lineHeight:1.45}}>
              📅 {routeChatBooking.runDate||"—"} · 約 {routeChatBooking.departTime||"—"} · 👥 {routeChatBooking.pax||1} 位
            </p>
            <p style={{color:TEXT,fontSize:15,margin:"8px 0 0"}}>📍 {routeChatBooking.pickup||"—"}</p>
            <p style={{color:TEXT,fontSize:15,margin:"4px 0 0"}}>🏁 {routeChatBooking.dest||"—"}</p>
          </Card>
          <ChatBox
            messages={routeChatMessages}
            onSend={sendRouteChatMessage}
            myId={auth.currentUser?.uid}
            myName={dname}
            h={340}
          />
        </Wrap>
      </div>
    );
  }

  // ══════════════════════════════════════════════════════════════════════
  // ── RENDER: LOGIN ──────────────────────────────────────────────────
  // ══════════════════════════════════════════════════════════════════════

  if(step==="login")return(
    <div style={{minHeight:"100vh",background:BG,paddingBottom:bottomNavLift}}>
      <Bar title={t("bar.driverLogin")} onBack={back}/>
      <Wrap>
        <div id="recaptcha-container"></div>
        <Card style={{background:"#EFF6FF",border:`1px solid ${TEAL}33`,marginBottom:12}}>
          <p style={{color:SUB,fontSize:15,lineHeight:1.55,margin:"0 0 10px"}}>
            <b style={{color:TEXT}}>統一帳戶：</b>請先喺首頁<b>「我要搵Carpool」</b>完成<b>註冊</b>，再用同一電郵／電話返嚟呢度<b>登入</b>，申請開通車主功能。通過審核（正式上線後需上載文件）先可以接單。
          </p>
          <Btn label="返回首頁" color={TEAL} onClick={()=>onBack?.()}/>
        </Card>
        <Card style={{background:"#FFF7ED",border:`1px solid ${WARN}33`}}>
          <p style={{color:WARN,fontSize:16,lineHeight:1.5,margin:0}}>⚠️ 車主申請須管理員審核；正式上線後需上載駕照等文件。</p>
        </Card>
        <Sec title="選擇方式">
          <div style={{display:"flex",gap:8,marginBottom:4}}>
            {[["email","📧 電郵"],["phone","📱 電話"]].map(([v,l])=>(
              <div key={v} onClick={()=>setLoginMethod(v)}
                style={{flex:1,textAlign:"center",padding:"12px",borderRadius:12,cursor:"pointer",
                  background:loginMethod===v?TEAL+"22":WHITE,
                  border:`1.5px solid ${loginMethod===v?TEAL:LIGHT}`,
                  color:loginMethod===v?TEAL:SUB,fontWeight:600,fontSize:17}}>{l}</div>
            ))}
          </div>
        </Sec>

        {loginMethod==="email"?(
          <Sec title="電郵登入（與乘客同一帳戶）">
            <Card>
              <Inp placeholder="電郵地址" value={email} onChange={e=>setEmail(e.target.value)} type="email"/>
              <div style={{position:"relative"}}>
                <Inp placeholder="密碼（最少6位）" value={password} onChange={e=>setPassword(e.target.value)}
                  type={showPw?"text":"password"} style={{paddingRight:44}}/>
                <span onClick={()=>setShowPw(!showPw)} style={{position:"absolute",right:12,top:12,cursor:"pointer",fontSize:18,userSelect:"none"}}>{showPw?"🙈":"👁️"}</span>
              </div>
              <p style={{textAlign:"right",margin:"4px 0 0",color:TEAL,fontSize:15,cursor:"pointer",fontWeight:600}}
                onClick={async()=>{
                  const emailTrimmed=(email||"").trim().toLowerCase();
                  if(!emailTrimmed){alert("請先輸入電郵地址");return;}
                  try{
                    const resetResults=await Promise.allSettled([
                      sendPasswordResetEmail(auth,emailTrimmed),
                      (async()=>{
                        const fn=httpsCallable(functions,"sendCustomPasswordResetEmailV1");
                        await fn({email:emailTrimmed});
                      })(),
                    ]);
                    const resetSent=resetResults.some(r=>r.status==="fulfilled");
                    if(!resetSent){
                      const reason=resetResults
                        .filter(r=>r.status==="rejected")
                        .map(r=>r.reason?.code||r.reason?.message||"unknown")
                        .join(" | ");
                      throw new Error(`all reset channels failed: ${reason}`);
                    }
                    alert("✅ 已發送重設密碼電郵，請檢查 Inbox / Spam。");
                  }catch(err){
                    if(err.code==="auth/user-not-found")alert("❌ 帳戶唔存在，請先喺「我要搵Carpool」註冊");
                    else if(err.code==="auth/invalid-email")alert("❌ 電郵格式唔正確");
                    else alert("❌ 發送失敗："+err.message);
                  }
                }}>
                忘記密碼？重設電郵密碼
              </p>
            </Card>
            <Btn label="登入 →"
              color={email&&password.length>=6?TEAL:"#CBD5E1"}
              disabled={!email||password.length<6}
              onClick={async()=>{
                try{
                  const uc=await signInWithEmailAndPassword(auth,email,password);
                  const dSnap=await getDoc(doc(db,"drivers",uc.user.uid));
                  if(!dSnap.exists()){
                    const pSnap=await getDoc(doc(db,"passengers",uc.user.uid));
                    if(!pSnap.exists()){
                      alert("請先喺「我要搵Carpool」完成註冊同個人資料，再返嚟申請車主。");
                      await auth.signOut();
                      onBack?.();
                      return;
                    }
                    applyPassengerDataToForm(pSnap.data(),uc.user);
                    setStep("register");
                    return;
                  }
                  const data=dSnap.data();
                  if(data.status==="pending"){setStep("pending");return;}
                  if(data.status==="rejected"){
                    alert("❌ 上次申請已拒絕，可以修改資料後重新提交。\n"+(data.rejectReason||""));
                    const pSnap=await getDoc(doc(db,"passengers",uc.user.uid));
                    if(pSnap.exists())applyPassengerDataToForm(pSnap.data(),uc.user);
                    setStep("register");
                    return;
                  }
                  loadDriverData(data);
                  requestNotificationPermission(uc.user.uid,"driver");
                  const hasActive=await restoreActiveOrder(uc.user.uid);
                  if(!hasActive)setStep("orders");
                }catch(err){
                  if(err.code==="auth/user-not-found")alert("❌ 帳戶唔存在，請先喺「我要搵Carpool」註冊");
                  else if(err.code==="auth/wrong-password")alert("❌ 密碼錯誤");
                  else alert("❌ 錯誤："+err.message);
                }
              }}/>
          </Sec>
        ):(
          <Sec title="電話登入（與乘客同一號碼）">
            <Card>
              <div style={{display:"flex",gap:8,marginBottom:10}}>
                <div style={{background:DC,border:`1.5px solid ${LIGHT}`,borderRadius:12,padding:"12px 14px",color:SUB,fontSize:17,whiteSpace:"nowrap"}}>🇨🇦 +1</div>
                <Inp placeholder="647-XXX-XXXX" value={dphone} onChange={e=>setDphone(e.target.value)} type="tel" maxLength={10}/>
              </div>
            </Card>
            <Btn label={dsmsCooldown>0?`重新發送 (${dsmsCooldown}秒)`:"發送驗證碼"}
              color={dphone.length>=10&&dsmsCooldown===0?TEAL:"#CBD5E1"}
              disabled={dphone.length<10||dsmsCooldown>0}
              onClick={async()=>{
                try{
                  const verifier=new RecaptchaVerifier(auth,"recaptcha-container",{size:"invisible"});
                  const result=await signInWithPhoneNumber(auth,`+1${dphone}`,verifier);
                  setDconfirmResult(result);setDsmsCooldown(30);setStep("driverSms");
                }catch(err){alert("❌ 發送失敗："+err.message);}
              }}/>
          </Sec>
        )}
      </Wrap>
    </div>
  );

  if(step==="driverSms")return(
    <div style={{minHeight:"100vh",background:BG,paddingBottom:bottomNavLift}}>
      <Bar title={t("bar.smsCode")} onBack={()=>setStep("login")}/>
      <Wrap>
        <p style={{color:SUB,fontSize:17,marginBottom:16}}>已發送至 +1 {dphone}</p>
        <Card><Inp placeholder="輸入6位驗證碼" value={code} onChange={e=>setCode(e.target.value)} type="number" maxLength={6}/></Card>
        <Btn label="確認" color={code.length>=6?TEAL:"#CBD5E1"} disabled={code.length<6}
          onClick={async()=>{
            if(!dconfirmResult)return;
            try{
              const uc=await dconfirmResult.confirm(code);
              const dSnap=await getDoc(doc(db,"drivers",uc.user.uid));
              if(!dSnap.exists()){
                const pSnap=await getDoc(doc(db,"passengers",uc.user.uid));
                if(!pSnap.exists()){
                  alert("請先喺「我要搵Carpool」用同一電話完成註冊同個人資料，再返嚟申請車主。");
                  await auth.signOut();
                  setStep("login");
                  return;
                }
                applyPassengerDataToForm(pSnap.data(),uc.user);
                setStep("register");
                return;
              }
              const data=dSnap.data();
              if(data.status==="pending"){setStep("pending");return;}
              if(data.status==="rejected"){
                alert("❌ 上次申請已拒絕，可以修改資料後重新提交。\n"+(data.rejectReason||""));
                const pSnap=await getDoc(doc(db,"passengers",uc.user.uid));
                if(pSnap.exists())applyPassengerDataToForm(pSnap.data(),uc.user);
                setStep("register");
                return;
              }
              loadDriverData(data);
              requestNotificationPermission(uc.user.uid,"driver");
              const hasActive=await restoreActiveOrder(uc.user.uid);
              if(!hasActive)setStep("orders");
            }catch(err){alert("❌ 驗證碼錯誤，請重試");}
          }}/>
      </Wrap>
    </div>
  );

  // ── REGISTER ───────────────────────────────────────────────────────
  if(step==="register")return(
    <div style={{minHeight:"100vh",background:BG,paddingBottom:bottomNavLift}}>
      <Bar title={t("bar.driverProfile")} onBack={back}/>
      <Wrap>
        <Card style={{background:"#ECFDF5",border:`1px solid ${GREEN}44`,marginBottom:12}}>
          <p style={{color:SUB,fontSize:15,margin:0,lineHeight:1.55}}>
            你正用<b>同一帳戶</b>申請<b>車主功能</b>。提交後由管理員審核；正式上線後需上載駕照／自拍等文件方可通過。
          </p>
        </Card>
        <Sec title="你嘅姓名"><Card><Inp placeholder="例：張師傅 / David" value={dname} onChange={e=>setDname(e.target.value)}/></Card></Sec>
        <Sec title="性別">
          <Card>
            <div style={{display:"flex",flexDirection:"column",gap:10}}>
              {D_GENDER_OPTIONS.map(({value,label,emoji})=>(
                <div key={value} onClick={()=>setGender(value)}
                  style={{
                    display:"flex",alignItems:"center",gap:14,padding:"14px 16px",borderRadius:14,cursor:"pointer",
                    border:`2px solid ${gender===value?TEAL:LIGHT}`,
                    background:gender===value?TEAL+"12":WHITE,
                  }}>
                  <span style={{fontSize:28}}>{emoji}</span>
                  <span style={{color:TEXT,fontSize:18,fontWeight:700}}>{label}</span>
                  <span style={{marginLeft:"auto",color:gender===value?TEAL:SUB,fontSize:20}}>{gender===value?"✓":""}</span>
                </div>
              ))}
            </div>
          </Card>
        </Sec>
        <Sec title="車牌號碼"><Card><Inp placeholder="例：BXYZ 456" value={plate} onChange={e=>setPlate(e.target.value.toUpperCase())}/></Card></Sec>
        <Sec title="聯絡電話（必填）">
          <Card>
            <div style={{display:"flex",gap:8}}>
              <div style={{background:DC,border:`1.5px solid ${LIGHT}`,borderRadius:12,padding:"12px 14px",color:SUB,fontSize:17,whiteSpace:"nowrap"}}>🇨🇦 +1</div>
              <Inp placeholder="647-XXX-XXXX" value={dphone} onChange={e=>setDphone(e.target.value)} type="tel" maxLength={10}/>
            </div>
          </Card>
        </Sec>
        <Sec title="語言">
          <div style={{display:"flex",gap:8,flexWrap:"wrap",marginBottom:8}}>
            {["廣東話","普通話","英語","法語"].map(l=>(
              <div key={l} onClick={()=>toggleLang(l)} style={{
                padding:"8px 14px",borderRadius:20,fontSize:16,cursor:"pointer",
                background:langs.includes(l)?TEAL+"22":WHITE,
                border:`1.5px solid ${langs.includes(l)?TEAL:LIGHT}`,
                color:langs.includes(l)?TEAL:SUB,fontWeight:600}}>
                {l==="廣東話"?"🇭🇰":l==="普通話"?"🇨🇳":l==="英語"?"🇬🇧":"🇫🇷"} {l}
              </div>
            ))}
          </div>
        </Sec>
        <Sec title="車型">
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
            {[["轎車","🚗","1–4人"],["SUV","🚙","1–6人"],["Van","🚐","1–8人"],["電動車","⚡","環保"]].map(([v,i,s])=>(
              <div key={v} onClick={()=>setVtype(v)} style={{
                background:vtype===v?TEAL+"22":WHITE,border:`1.5px solid ${vtype===v?TEAL:LIGHT}`,
                borderRadius:12,padding:"12px",cursor:"pointer",display:"flex",alignItems:"center",gap:8}}>
                <span style={{fontSize:22}}>{i}</span>
                <div><div style={{color:vtype===v?TEAL:TEXT,fontSize:17,fontWeight:600}}>{v}</div>
                <div style={{color:SUB,fontSize:14}}>{s}</div></div>
              </div>
            ))}
          </div>
        </Sec>

        <Sec title="車輛：年份 · 品牌 · 型號 · 配置">
          <Card>
            <p style={{color:SUB,fontSize:14,margin:"0 0 12px",lineHeight:1.5}}>
              預設用美國 EPA 公開資料庫（英文）逐級揀選；揀齊「配置／引擎」可對應準確油耗。北美常見車款最齊。
            </p>
            <button type="button" onClick={()=>{
              setVehiclePickerManual(w=>{
                if(!w){setVehicleEpaId("");setVehicleTrim("");}
                return !w;
              });
              setEpaMenuErr("");
            }}
              style={{marginBottom:12,background:DC,border:`1px solid ${LIGHT}`,borderRadius:10,padding:"10px 14px",fontSize:15,color:TEAL,fontWeight:600,cursor:"pointer",width:"100%",textAlign:"center"}}>
              {vehiclePickerManual?"改用 EPA 下拉選單":"改用手動輸入品牌／型號"}
            </button>
            {epaMenuErr?<p style={{color:WARN,fontSize:14,margin:"0 0 10px"}}>{epaMenuErr}</p>:null}
            {vehiclePickerManual?(
              <>
                <Sel value={vehicleYear} onChange={e=>setVehicleYear(e.target.value)} style={{marginBottom:12}}>
                  <option value="">— 年份 —</option>
                  {STATIC_YEARS.map(y=><option key={y} value={y}>{y}</option>)}
                </Sel>
                <Inp placeholder="品牌（例 Toyota）" value={vehicleMake} onChange={e=>setVehicleMake(e.target.value)} style={{marginBottom:12}}/>
                <Inp placeholder="型號（可連配置一齊打）" value={vehicleModel} onChange={e=>setVehicleModel(e.target.value)} style={{marginBottom:12}}/>
                <Inp placeholder="配置／trim（選填）" value={vehicleTrim} onChange={e=>setVehicleTrim(e.target.value)} style={{marginBottom:0}}/>
              </>
            ):(
              <>
                <Sel value={vehicleYear} onChange={e=>{setVehicleYear(e.target.value);setVehicleMake("");setVehicleModel("");setVehicleTrim("");setVehicleEpaId("");}} disabled={epaLoad.years&&epaYearRows.length===0} style={{marginBottom:12}}>
                  <option value="">{epaLoad.years&&epaYearRows.length===0?"載入年份…":"— 年份 —"}</option>
                  {epaYearRows.map(r=><option key={r.value} value={r.value}>{r.text}</option>)}
                </Sel>
                <Sel value={vehicleMake} onChange={e=>{setVehicleMake(e.target.value);setVehicleModel("");setVehicleTrim("");setVehicleEpaId("");}} disabled={!parseInt(vehicleYear,10)||epaLoad.makes} style={{marginBottom:12}}>
                  <option value="">{epaLoad.makes?"載入品牌…":"— 品牌 —"}</option>
                  {epaMakeRows.map(r=><option key={r.value} value={r.value}>{r.text}</option>)}
                </Sel>
                <Sel value={vehicleModel} onChange={e=>{setVehicleModel(e.target.value);setVehicleTrim("");setVehicleEpaId("");}} disabled={!(vehicleMake||"").trim()||epaLoad.models} style={{marginBottom:12}}>
                  <option value="">{epaLoad.models?"載入型號…":"— 型號（EPA） —"}</option>
                  {epaModelRows.map(r=><option key={r.value} value={r.value}>{r.text}</option>)}
                </Sel>
                <Sel value={vehicleEpaId} onChange={e=>{
                  const v=e.target.value;
                  setVehicleEpaId(v);
                  const row=epaTrimRows.find(x=>x.value===v);
                  setVehicleTrim(row?row.text:"");
                }} disabled={!(vehicleModel||"").trim()||epaLoad.trims} style={{marginBottom:0}}>
                  <option value="">{epaLoad.trims?"載入配置…":"— 配置／引擎（trim） —"}</option>
                  {epaTrimRows.map(r=><option key={r.value} value={r.value}>{r.text}</option>)}
                </Sel>
              </>
            )}
          </Card>
        </Sec>
        <Card style={{background:"#FFFBEB",border:`1px solid ${GOLD}33`}}>
          <p style={{color:TEXT,fontWeight:700,fontSize:16,margin:"0 0 8px"}}>⛽ 綜合油耗（L/100km）</p>
          <p style={{color:SUB,fontSize:14,margin:"0 0 10px",lineHeight:1.5}}>
            選單模式會按你所揀嘅配置讀 EPA；手動模式會用品牌＋型號試配。你可隨時用手動輸入 L/100km 覆蓋。
          </p>
          <Inp placeholder="手動輸入 L/100km（可留空）" value={manualLiters100} onChange={e=>setManualLiters100(e.target.value)} style={{marginBottom:10}}/>
          <Btn label={fuelLookupBusy?"計算緊…":"估算綜合油耗"} color={TEAL} disabled={fuelLookupBusy} onClick={runFuelLookup}/>
          {litersPer100km!=null&&(
            <p style={{color:TEAL,fontWeight:700,margin:"10px 0 0",fontSize:17}}>
              ≈ {litersPer100km} L/100km
              <span style={{color:SUB,fontWeight:500,fontSize:14,marginLeft:8}}>
                （{fuelEconomySource==="epa"?"EPA":fuelEconomySource==="manual"?"手動":fuelEconomySource==="heuristic"?"估算":fuelEconomySource==="ev"?"電動":""}）
              </span>
            </p>
          )}
          {vtype==="電動車"&&(
            <p style={{color:SUB,fontSize:14,margin:"8px 0 0"}}>電動車唔計汽油；油費估算會顯示「—」。</p>
          )}
        </Card>

        {DRIVER_UPLOADS_DISABLED && (
          <Card style={{background:"#F1F5F9",border:`1px solid #CBD5E1`,marginBottom:12}}>
            <p style={{color:TEXT,fontSize:16,fontWeight:700,margin:"0 0 6px"}}>📋 原型階段</p>
            <p style={{color:SUB,fontSize:15,margin:0,lineHeight:1.55}}>
              自拍上載暫停使用，介面會保留。App 正式上線後將需要清晰正面大頭相作頭像同審核。
            </p>
          </Card>
        )}
        <Sec title={DRIVER_UPLOADS_DISABLED?"正面人像自拍（正式上線後啟用）":"正面人像自拍（必填）"}><Card style={{opacity:DRIVER_UPLOADS_DISABLED?0.72:1}}>
          <div style={{display:"flex",alignItems:"center",gap:14}}>
            <div style={{width:70,height:70,borderRadius:"50%",background:DC,display:"flex",alignItems:"center",justifyContent:"center",fontSize:32,border:`2px solid ${LIGHT}`,overflow:"hidden",flexShrink:0}}>
              {selfiePreview?<img src={selfiePreview} style={{width:"100%",height:"100%",objectFit:"cover"}} alt="selfie"/>:"👤"}
            </div>
            <div style={{flex:1}}>
              <p style={{color:TEXT,fontSize:17,fontWeight:600,margin:"0 0 4px"}}>請影清晰正面大頭相</p>
              <p style={{color:SUB,fontSize:15,margin:"0 0 8px",lineHeight:1.5}}>
                用作<b>個人頭像</b>（乘客會見到）同<b>管理員審核存檔</b>，請勿用風景或駕照代替。
              </p>
              <input ref={fileRef} type="file" accept="image/*" capture="user" onChange={handleSelfie} style={{display:"none"}} disabled={DRIVER_UPLOADS_DISABLED}/>
              <button type="button" disabled={DRIVER_UPLOADS_DISABLED} onClick={()=>!DRIVER_UPLOADS_DISABLED&&fileRef.current?.click()}
                style={{
                  background:DRIVER_UPLOADS_DISABLED?"#E2E8F0":TEAL+"15",
                  border:DRIVER_UPLOADS_DISABLED?`1px solid #CBD5E1`:`1px solid ${TEAL}44`,
                  color:DRIVER_UPLOADS_DISABLED?"#64748B":TEAL,borderRadius:10,padding:"7px 16px",fontSize:16,fontWeight:600,
                  cursor:DRIVER_UPLOADS_DISABLED?"not-allowed":"pointer",
                }}>
                {DRIVER_UPLOADS_DISABLED?"暫停上載（原型）":(selfiePreview?"更換自拍 📸":"拍照／上載自拍 📸")}
              </button>
            </div>
          </div>
        </Card></Sec>
        <Sec title="個人簡介（選填）"><Card>
          <textarea value={bio} onChange={e=>e.target.value.length<=100&&setBio(e.target.value)}
            placeholder="例：開車15年，熟悉GTA路線，鍾意同人傾偈 😊"
            style={{width:"100%",background:DC,border:`1.5px solid ${LIGHT}`,borderRadius:10,padding:"10px 12px",fontSize:16,color:TEXT,outline:"none",resize:"none",height:80,lineHeight:1.6}}/>
          <p style={{color:bio.length>80?WARN:SUB,fontSize:14,textAlign:"right"}}>{bio.length}/100</p>
        </Card></Sec>

        {/* ── SOCIAL PROFILE: Coffee Chat ─────────────────────────── */}
        <Card style={{background:"#FFFBEB",border:`1px solid ${GOLD}33`}}>
          <p style={{color:GOLD,fontWeight:700,fontSize:17,margin:"0 0 4px"}}>☕ Coffee Chat 社交配對</p>
          <p style={{color:SUB,fontSize:15,margin:"0 0 12px"}}>填寫你嘅職業同興趣，乘客可以揀有共同話題嘅車主</p>
          <Sec title="職業">
            <div style={{display:"flex",gap:6,flexWrap:"wrap",marginBottom:8}}>
              {OCCUPATION_PRESETS.slice(0,12).map(o=>(
                <div key={o} onClick={()=>setOccupation(o)} style={{
                  padding:"6px 12px",borderRadius:20,fontSize:15,cursor:"pointer",
                  background:occupation===o?TEAL+"22":WHITE,
                  border:`1.5px solid ${occupation===o?TEAL:LIGHT}`,
                  color:occupation===o?TEAL:SUB}}>{o}</div>
              ))}
            </div>
            <Inp placeholder="或自訂職業..." value={OCCUPATION_PRESETS.includes(occupation)?"":occupation}
              onChange={e=>setOccupation(e.target.value)} style={{marginBottom:0}}/>
          </Sec>
          <InterestPicker title="專長" presets={EXPERTISE_PRESETS} selected={expertise} onChange={setExpertise} max={5}/>
          <InterestPicker title="嗜好" presets={HOBBY_PRESETS} selected={hobbies} onChange={setHobbies} max={5}/>
        </Card>

        <Sec title="接唔接細路？"><Card>
          <Toggle on={acceptChild} onToggle={()=>setAcceptChild(!acceptChild)} icon="👶" label="接受有細路嘅乘客"/>
          {acceptChild&&(
            <div className="go-fade" style={{marginTop:12,paddingLeft:32}}>
              {[["rear","👶","後向嬰兒座椅"],["forward","🧒","前向兒童座椅"],["booster","👦","加高座椅"]].map(([k,i,l])=>(
                <div key={k} style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:10,background:DC,borderRadius:10,padding:"10px 12px"}}>
                  <div style={{color:TEXT,fontSize:16}}>{i} {l}</div>
                  <div style={{display:"flex",alignItems:"center",gap:10}}>
                    <button onClick={()=>setChildSeats(p=>({...p,[k]:Math.max(0,p[k]-1)}))} style={{width:28,height:28,borderRadius:"50%",border:`1px solid ${LIGHT}`,background:WHITE,cursor:"pointer",fontSize:18,display:"flex",alignItems:"center",justifyContent:"center",color:TEAL}}>−</button>
                    <span style={{color:TEXT,fontWeight:700,minWidth:16,textAlign:"center"}}>{childSeats[k]}</span>
                    <button onClick={()=>setChildSeats(p=>({...p,[k]:p[k]+1}))} style={{width:28,height:28,borderRadius:"50%",border:"none",background:TEAL,cursor:"pointer",fontSize:18,display:"flex",alignItems:"center",justifyContent:"center",color:WHITE}}>+</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card></Sec>
        <Sec title="接唔接寵物？"><Card>
          <Toggle on={acceptPet} onToggle={()=>setAcceptPet(!acceptPet)} icon="🐾" label="接受有寵物嘅乘客"/>
          {acceptPet&&(
            <div className="go-fade" style={{marginTop:10,paddingLeft:32,display:"flex",gap:8,flexWrap:"wrap"}}>
              {[["small","🐩","細狗/貓"],["big","🐕","大狗（有籠）"],["other","🐾","其他"]].map(([v,i,l])=>(
                <div key={v} onClick={()=>togglePetType(v)} style={{padding:"7px 13px",borderRadius:20,fontSize:15,cursor:"pointer",
                  background:petTypes.includes(v)?WARN+"22":DC,border:`1.5px solid ${petTypes.includes(v)?WARN:LIGHT}`,color:petTypes.includes(v)?WARN:SUB}}>{i} {l}</div>
              ))}
            </div>
          )}
        </Card></Sec>
        <Btn label="下一步：上載駕照 →"
          color={dname.trim().length>=2&&plate.trim().length>=2&&langs.length>0&&gender&&(DRIVER_UPLOADS_DISABLED||selfie)&&vehicleStepOk()?TEAL:"#CBD5E1"}
          disabled={dname.trim().length<2||plate.trim().length<2||langs.length===0||dphone.length<10||!gender||(!DRIVER_UPLOADS_DISABLED&&!selfie)||!vehicleStepOk()}
          onClick={()=>setStep("docs")}/>
      </Wrap>
    </div>
  );

  // ── DOCS UPLOAD：駕照正面 + 背面 ────────────────────────────────────
  if(step==="docs")return(
    <div style={{minHeight:"100vh",background:BG,paddingBottom:bottomNavLift}}>
      <Bar title={t("bar.uploadLicense")} onBack={back}/>
      <Wrap>
        {DRIVER_UPLOADS_DISABLED ? (
          <Card style={{background:"#F1F5F9",border:`1px solid #CBD5E1`,marginBottom:16}}>
            <p style={{color:TEXT,fontSize:16,fontWeight:700,margin:"0 0 6px"}}>📋 原型階段 · 檔案上載暫停</p>
            <p style={{color:SUB,fontSize:15,margin:0,lineHeight:1.55}}>
              而家唔使上載駕照；下面區塊會保留，等 App 正式上線後再要求你補交以完成審核。你可直接按「提交審核」送出資料。
            </p>
          </Card>
        ) : (
          <p style={{color:SUB,fontSize:16,marginBottom:16,lineHeight:1.55}}>
            請分別上載駕照<b>正面</b>同<b>背面</b>清晰相片（或 PDF）。正面人像自拍已喺上一步完成。
          </p>
        )}

        <div style={{
          opacity:DRIVER_UPLOADS_DISABLED?0.62:1,
          pointerEvents:DRIVER_UPLOADS_DISABLED?"none":"auto",
          filter:DRIVER_UPLOADS_DISABLED?"grayscale(0.35)":undefined,
        }}>
          <div style={{background:licenseFrontPreview?"#ECFDF5":WHITE,
            border:`1.5px dashed ${DRIVER_UPLOADS_DISABLED?"#94A3B8":licenseFrontPreview?GREEN:LIGHT}`,borderRadius:14,padding:"14px 16px",marginBottom:10}}>
            <div style={{display:"flex",alignItems:"center",gap:12}}>
              <div style={{width:44,height:44,borderRadius:"50%",background:licenseFrontPreview?"#D1FAE5":DC,
                display:"flex",alignItems:"center",justifyContent:"center",fontSize:20,flexShrink:0}}>
                {licenseFrontPreview?"✅":"🪪"}
              </div>
              <div style={{flex:1}}>
                <p style={{color:licenseFrontPreview?GREEN:TEXT,fontWeight:600,fontSize:17,margin:0}}>駕照正面</p>
                <p style={{color:SUB,fontSize:15,margin:"2px 0 0"}}>{licenseFrontPreview?"已上載 ✓":"有相／姓名嗰面"}</p>
              </div>
              <div>
                <input type="file" accept="image/*,application/pdf" id="file-license-front" onChange={handleLicenseFront} style={{display:"none"}} disabled={DRIVER_UPLOADS_DISABLED}/>
                <button type="button" disabled={DRIVER_UPLOADS_DISABLED} onClick={()=>!DRIVER_UPLOADS_DISABLED&&document.getElementById("file-license-front")?.click()}
                  style={{
                    background:DRIVER_UPLOADS_DISABLED?"#CBD5E1":licenseFrontPreview?GREEN+"15":TEAL,border:"none",
                    color:DRIVER_UPLOADS_DISABLED?"#64748B":licenseFrontPreview?GREEN:WHITE,borderRadius:8,padding:"6px 12px",fontSize:15,fontWeight:600,
                    cursor:DRIVER_UPLOADS_DISABLED?"not-allowed":"pointer",
                  }}>
                  {DRIVER_UPLOADS_DISABLED?"暫停":licenseFrontPreview?"更換":"上載"}
                </button>
              </div>
            </div>
          </div>
          {licenseFrontPreview&&/^data:image/.test(licenseFrontPreview)&&(
            <img src={licenseFrontPreview} style={{width:"100%",borderRadius:12,maxHeight:180,objectFit:"contain",marginBottom:10}} alt="license front"/>
          )}

          <div style={{background:licenseBackPreview?"#ECFDF5":WHITE,
            border:`1.5px dashed ${DRIVER_UPLOADS_DISABLED?"#94A3B8":licenseBackPreview?GREEN:LIGHT}`,borderRadius:14,padding:"14px 16px",marginBottom:12}}>
            <div style={{display:"flex",alignItems:"center",gap:12}}>
              <div style={{width:44,height:44,borderRadius:"50%",background:licenseBackPreview?"#D1FAE5":DC,
                display:"flex",alignItems:"center",justifyContent:"center",fontSize:20,flexShrink:0}}>
                {licenseBackPreview?"✅":"🪪"}
              </div>
              <div style={{flex:1}}>
                <p style={{color:licenseBackPreview?GREEN:TEXT,fontWeight:600,fontSize:17,margin:0}}>駕照背面</p>
                <p style={{color:SUB,fontSize:15,margin:"2px 0 0"}}>{licenseBackPreview?"已上載 ✓":"地址／條碼嗰面"}</p>
              </div>
              <div>
                <input type="file" accept="image/*,application/pdf" id="file-license-back" onChange={handleLicenseBack} style={{display:"none"}} disabled={DRIVER_UPLOADS_DISABLED}/>
                <button type="button" disabled={DRIVER_UPLOADS_DISABLED} onClick={()=>!DRIVER_UPLOADS_DISABLED&&document.getElementById("file-license-back")?.click()}
                  style={{
                    background:DRIVER_UPLOADS_DISABLED?"#CBD5E1":licenseBackPreview?GREEN+"15":TEAL,border:"none",
                    color:DRIVER_UPLOADS_DISABLED?"#64748B":licenseBackPreview?GREEN:WHITE,borderRadius:8,padding:"6px 12px",fontSize:15,fontWeight:600,
                    cursor:DRIVER_UPLOADS_DISABLED?"not-allowed":"pointer",
                  }}>
                  {DRIVER_UPLOADS_DISABLED?"暫停":licenseBackPreview?"更換":"上載"}
                </button>
              </div>
            </div>
          </div>
          {licenseBackPreview&&/^data:image/.test(licenseBackPreview)&&(
            <img src={licenseBackPreview} style={{width:"100%",borderRadius:12,maxHeight:180,objectFit:"contain",marginBottom:12}} alt="license back"/>
          )}
        </div>

        {uploading&&<LoadingOverlay msg={DRIVER_UPLOADS_DISABLED?"提交中，請稍等...":"上傳中，請稍等..."}/>}
        <Btn label={DRIVER_UPLOADS_DISABLED?"提交審核（原型暫免上載）→":licenseFrontFile&&licenseBackFile?"提交審核 →":"請上載正面同背面"}
          color={DRIVER_UPLOADS_DISABLED||licenseFrontFile&&licenseBackFile?TEAL:"#CBD5E1"}
          disabled={DRIVER_UPLOADS_DISABLED?false:!licenseFrontFile||!licenseBackFile}
          onClick={handleSubmit}/>
      </Wrap>
    </div>
  );

  if(step==="pending")return(
    <div style={{minHeight:"100vh",background:BG,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",padding:24,paddingBottom:24+bottomNavLift}}>
      <div style={{fontSize:64,marginBottom:16}}>⏳</div>
      <p style={{color:TEXT,fontSize:20,fontWeight:700,marginBottom:8}}>資料已上傳，審核中</p>
      <p style={{color:SUB,fontSize:17,textAlign:"center",marginBottom:24}}>管理員會喺24小時內審核你嘅申請。</p>
      <Btn label="返回主頁" color={TEAL} style={{maxWidth:200}} onClick={onBack}/>
    </div>
  );

  // ══════════════════════════════════════════════════════════════════════
  // ── MY PROFILE ─────────────────────────────────────────────────────
  // ══════════════════════════════════════════════════════════════════════

  if(step==="my_profile")return(
    <div style={{minHeight:"100vh",background:BG,paddingBottom:contentPadBottom}}>
      <Bar title={t("bar.profile")} onBack={()=>handleNavTab("orders")}/>
      <Wrap>
        <Card style={{textAlign:"center",padding:24}}>
          <div style={{width:80,height:80,borderRadius:"50%",background:DC,overflow:"hidden",
            display:"flex",alignItems:"center",justifyContent:"center",fontSize:36,margin:"0 auto 12px",border:`2px solid ${LIGHT}`}}>
            {selfiePreview?<img src={selfiePreview} style={{width:"100%",height:"100%",objectFit:"cover"}} alt=""/>:"👤"}
          </div>
          <p style={{color:TEXT,fontWeight:700,fontSize:18,margin:"0 0 4px"}}>{dname}</p>
          {gender&&<p style={{color:SUB,fontSize:16,margin:"0 0 4px"}}>⚧ {dGenderLabel(gender)}</p>}
          <p style={{color:SUB,fontSize:16,margin:"0 0 2px"}}>📧 {email||"未設定"}</p>
          <p style={{color:SUB,fontSize:16,margin:"0 0 2px"}}>📱 {dphone?`+1 ${dphone}`:"未設定"}</p>
          <p style={{color:SUB,fontSize:16,margin:"0 0 2px"}}>🚗 {plate} · {vtype}</p>
          {(vehicleYear||vehicleMake||vehicleModel||vehicleTrim)&&(
            <p style={{color:SUB,fontSize:15,margin:"6px 0 0"}}>📅 {vehicleYear||"—"} {vehicleMake} {vehicleModel}{vehicleTrim?` · ${vehicleTrim}`:""}</p>
          )}
          {litersPer100km!=null&&vtype!=="電動車"&&(
            <p style={{color:TEAL,fontSize:15,margin:"4px 0 0",fontWeight:600}}>⛽ 約 {litersPer100km} L/100km（{fuelEconomySource==="epa"?"EPA":fuelEconomySource==="manual"?"手動":"估算"}）</p>
          )}
          <StarDisplay rating={avgRating} count={ratingCount}/>
        </Card>

        {/* Social interests display */}
        {(occupation||expertise.length>0||hobbies.length>0)&&(
          <Card style={{background:"#FFFBEB",border:`1px solid ${GOLD}33`}}>
            <p style={{color:GOLD,fontWeight:700,fontSize:16,margin:"0 0 8px"}}>☕ Coffee Chat 簡介</p>
            <InterestTags occupation={occupation} expertise={expertise} hobbies={hobbies}/>
          </Card>
        )}

        <Sec title="更新電話號碼">
          <Card>
            <div style={{display:"flex",gap:8}}>
              <div style={{background:DC,border:`1.5px solid ${LIGHT}`,borderRadius:12,padding:"12px 14px",color:SUB,fontSize:17,whiteSpace:"nowrap"}}>🇨🇦 +1</div>
              <Inp placeholder={dphone||"647-XXX-XXXX"} value={editPhone} onChange={e=>setEditPhone(e.target.value)} type="tel" maxLength={10}/>
            </div>
            <Btn label="更新電話" color={editPhone.length>=10?TEAL:"#CBD5E1"} disabled={editPhone.length<10} onClick={saveDriverPhone} style={{marginTop:4}}/>
          </Card>
        </Sec>

        <Sec title="語言">
          <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
            {langs.map(l=>(<Tag key={l} label={l} color={TEAL}/>))}
          </div>
        </Sec>
      </Wrap>
      <BottomNav active={navTab} onChange={handleNavTab} items={bottomNavItems} bottomInset={bottomNavLift}/>
    </div>
  );

  // ══════════════════════════════════════════════════════════════════════
  // ── SETTINGS ───────────────────────────────────────────────────────
  // ══════════════════════════════════════════════════════════════════════

  if(step==="settings"){
    if(settingsSub==="profile")return(
      <div style={{minHeight:"100vh",background:BG,paddingBottom:contentPadBottom}}>
        <Bar title={t("bar.editProfile")} onBack={()=>setSettingsSub(null)}/>
        <Wrap>
          <Sec title="個人簡介"><Card>
            <textarea value={bio} onChange={e=>e.target.value.length<=100&&setBio(e.target.value)} placeholder="自我介紹..."
              style={{width:"100%",background:DC,border:`1.5px solid ${LIGHT}`,borderRadius:10,padding:"10px 12px",fontSize:16,color:TEXT,outline:"none",resize:"none",height:80}}/>
            <p style={{color:SUB,fontSize:14,textAlign:"right"}}>{bio.length}/100</p>
          </Card></Sec>

          {/* Coffee Chat profile editing */}
          <Card style={{background:"#FFFBEB",border:`1px solid ${GOLD}33`}}>
            <p style={{color:GOLD,fontWeight:700,fontSize:17,margin:"0 0 8px"}}>☕ Coffee Chat 社交配對</p>
            <Sec title="職業">
              <div style={{display:"flex",gap:6,flexWrap:"wrap",marginBottom:8}}>
                {OCCUPATION_PRESETS.slice(0,12).map(o=>(
                  <div key={o} onClick={()=>setOccupation(o)} style={{
                    padding:"5px 10px",borderRadius:20,fontSize:14,cursor:"pointer",
                    background:occupation===o?TEAL+"22":WHITE,
                    border:`1.5px solid ${occupation===o?TEAL:LIGHT}`,
                    color:occupation===o?TEAL:SUB}}>{o}</div>
                ))}
              </div>
              <Inp placeholder="或自訂職業..." value={OCCUPATION_PRESETS.includes(occupation)?"":occupation}
                onChange={e=>setOccupation(e.target.value)} style={{marginBottom:0}}/>
            </Sec>
            <InterestPicker title="專長" presets={EXPERTISE_PRESETS} selected={expertise} onChange={setExpertise} max={5}/>
            <InterestPicker title="嗜好" presets={HOBBY_PRESETS} selected={hobbies} onChange={setHobbies} max={5}/>
          </Card>

          <Sec title="車輛：年份 · 品牌 · 型號 · 配置"><Card>
            <button type="button" onClick={()=>{
              setVehiclePickerManual(w=>{
                if(!w){setVehicleEpaId("");setVehicleTrim("");}
                return !w;
              });
              setEpaMenuErr("");
            }}
              style={{marginBottom:10,background:DC,border:`1px solid ${LIGHT}`,borderRadius:10,padding:"8px 12px",fontSize:14,color:TEAL,fontWeight:600,cursor:"pointer",width:"100%",textAlign:"center"}}>
              {vehiclePickerManual?"改用 EPA 下拉選單":"改用手動輸入"}
            </button>
            {epaMenuErr?<p style={{color:WARN,fontSize:13,margin:"0 0 8px"}}>{epaMenuErr}</p>:null}
            {vehiclePickerManual?(
              <>
                <Sel value={vehicleYear} onChange={e=>setVehicleYear(e.target.value)} style={{marginBottom:8}}>
                  <option value="">— 年份 —</option>
                  {STATIC_YEARS.map(y=><option key={y} value={y}>{y}</option>)}
                </Sel>
                <Inp placeholder="品牌" value={vehicleMake} onChange={e=>setVehicleMake(e.target.value)} style={{marginBottom:8}}/>
                <Inp placeholder="型號" value={vehicleModel} onChange={e=>setVehicleModel(e.target.value)} style={{marginBottom:8}}/>
                <Inp placeholder="配置（選填）" value={vehicleTrim} onChange={e=>setVehicleTrim(e.target.value)} style={{marginBottom:0}}/>
              </>
            ):(
              <>
                <Sel value={vehicleYear} onChange={e=>{setVehicleYear(e.target.value);setVehicleMake("");setVehicleModel("");setVehicleTrim("");setVehicleEpaId("");}} disabled={epaLoad.years&&epaYearRows.length===0} style={{marginBottom:8}}>
                  <option value="">{epaLoad.years&&epaYearRows.length===0?"載入年份…":"— 年份 —"}</option>
                  {epaYearRows.map(r=><option key={r.value} value={r.value}>{r.text}</option>)}
                </Sel>
                <Sel value={vehicleMake} onChange={e=>{setVehicleMake(e.target.value);setVehicleModel("");setVehicleTrim("");setVehicleEpaId("");}} disabled={!parseInt(vehicleYear,10)||epaLoad.makes} style={{marginBottom:8}}>
                  <option value="">{epaLoad.makes?"載入品牌…":"— 品牌 —"}</option>
                  {epaMakeRows.map(r=><option key={r.value} value={r.value}>{r.text}</option>)}
                </Sel>
                <Sel value={vehicleModel} onChange={e=>{setVehicleModel(e.target.value);setVehicleTrim("");setVehicleEpaId("");}} disabled={!(vehicleMake||"").trim()||epaLoad.models} style={{marginBottom:8}}>
                  <option value="">{epaLoad.models?"載入型號…":"— 型號 —"}</option>
                  {epaModelRows.map(r=><option key={r.value} value={r.value}>{r.text}</option>)}
                </Sel>
                <Sel value={vehicleEpaId} onChange={e=>{
                  const v=e.target.value;
                  setVehicleEpaId(v);
                  const row=epaTrimRows.find(x=>x.value===v);
                  setVehicleTrim(row?row.text:"");
                }} disabled={!(vehicleModel||"").trim()||epaLoad.trims} style={{marginBottom:0}}>
                  <option value="">{epaLoad.trims?"載入配置…":"— 配置／trim —"}</option>
                  {epaTrimRows.map(r=><option key={r.value} value={r.value}>{r.text}</option>)}
                </Sel>
              </>
            )}
          </Card></Sec>
          <Card style={{background:"#FFFBEB",border:`1px solid ${GOLD}33`}}>
            <Inp placeholder="手動 L/100km（可留空）" value={manualLiters100} onChange={e=>setManualLiters100(e.target.value)} style={{marginBottom:8}}/>
            <Btn label={fuelLookupBusy?"計算緊…":"重新估算油耗"} color={WARN} textColor={WHITE} disabled={fuelLookupBusy} onClick={runFuelLookup}/>
            {litersPer100km!=null&&<p style={{color:TEAL,fontWeight:700,margin:"8px 0 0"}}>≈ {litersPer100km} L/100km</p>}
          </Card>

          <Sec title="兒童座椅"><Card>
            <Toggle on={acceptChild} onToggle={()=>setAcceptChild(!acceptChild)} icon="👶" label="接受有細路嘅乘客"/>
            {acceptChild&&(
              <div style={{marginTop:8}}>
                {[["rear","👶","後向"],["forward","🧒","前向"],["booster","👦","加高"]].map(([k,i,l])=>(
                  <div key={k} style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:8,background:DC,borderRadius:10,padding:"8px 12px"}}>
                    <span style={{color:TEXT,fontSize:16}}>{i} {l}</span>
                    <div style={{display:"flex",alignItems:"center",gap:8}}>
                      <button onClick={()=>setChildSeats(p=>({...p,[k]:Math.max(0,p[k]-1)}))} style={{width:26,height:26,borderRadius:"50%",border:`1px solid ${LIGHT}`,background:WHITE,cursor:"pointer",fontSize:17,display:"flex",alignItems:"center",justifyContent:"center",color:TEAL}}>−</button>
                      <span style={{fontWeight:700,minWidth:14,textAlign:"center"}}>{childSeats[k]}</span>
                      <button onClick={()=>setChildSeats(p=>({...p,[k]:p[k]+1}))} style={{width:26,height:26,borderRadius:"50%",border:"none",background:TEAL,cursor:"pointer",fontSize:17,display:"flex",alignItems:"center",justifyContent:"center",color:WHITE}}>+</button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card></Sec>
          <Sec title="寵物"><Card>
            <Toggle on={acceptPet} onToggle={()=>setAcceptPet(!acceptPet)} icon="🐾" label="接受寵物"/>
            {acceptPet&&(
              <div style={{display:"flex",gap:8,flexWrap:"wrap",marginTop:8}}>
                {[["small","🐩","細"],["big","🐕","大"],["other","🐾","其他"]].map(([v,i,l])=>(
                  <div key={v} onClick={()=>togglePetType(v)} style={{padding:"6px 12px",borderRadius:20,fontSize:15,cursor:"pointer",
                    background:petTypes.includes(v)?WARN+"22":DC,border:`1.5px solid ${petTypes.includes(v)?WARN:LIGHT}`,color:petTypes.includes(v)?WARN:SUB}}>{i} {l}</div>
                ))}
              </div>
            )}
          </Card></Sec>
          <Btn label="儲存" onClick={saveDriverProfile}/>
        </Wrap>
        <BottomNav active={navTab} onChange={handleNavTab} items={bottomNavItems} bottomInset={bottomNavLift}/>
      </div>
    );

    if(settingsSub==="my_routes")return(
      <div style={{minHeight:"100vh",background:BG,paddingBottom:contentPadBottom}}>
        <Bar title={t("bar.fixedRoutes")} onBack={()=>setSettingsSub(null)}/>
        <Wrap>
          <Card style={{background:"#F0FDFA",border:`1px solid ${TEAL}33`}}>
            <p style={{color:TEAL,fontSize:16,margin:0,lineHeight:1.55}}>
              可設<b>每週重複</b>或<b>單次日期</b>；須填<b>每位乘客收費</b>（加元），系統會顯示 GTA 網上共乘參考價。乘客即時見到收費同<b>剩餘座位</b>。停用唔會刪除舊紀錄。
            </p>
          </Card>

          <Sec title="新增路線">
            <Card>
              <p style={{color:SUB,fontSize:15,margin:"0 0 8px"}}>路線類型</p>
              <div style={{display:"flex",gap:8,marginBottom:14}}>
                {[
                  {k:"weekly",l:"每週重複",sub:"揀星期，未來自動對應日子"},
                  {k:"oneOff",l:"指定單次",sub:"只開某一個出發日"},
                ].map(({k,l,sub})=>(
                  <button key={k} type="button" onClick={()=>setNewRouteKind(k)}
                    style={{
                      flex:1,textAlign:"left",padding:"10px 12px",borderRadius:12,cursor:"pointer",
                      border:`2px solid ${newRouteKind===k?TEAL:LIGHT}`,
                      background:newRouteKind===k?TEAL+"18":WHITE,
                    }}>
                    <span style={{display:"block",fontSize:16,fontWeight:700,color:newRouteKind===k?TEAL:TEXT}}>{l}</span>
                    <span style={{display:"block",fontSize:12,color:SUB,marginTop:2,lineHeight:1.35}}>{sub}</span>
                  </button>
                ))}
              </div>
              {newRouteKind==="oneOff"?(
                <>
                  <p style={{color:SUB,fontSize:15,margin:"0 0 6px"}}>指定出發日期</p>
                  <input type="date" min={todayIsoLocal()} value={newRouteRunDate} onChange={e=>setNewRouteRunDate(e.target.value)}
                    style={{width:"100%",padding:"12px 14px",borderRadius:12,border:`1.5px solid ${LIGHT}`,fontSize:17,marginBottom:14,color:TEXT}}/>
                </>
              ):(
                <>
                  <p style={{color:SUB,fontSize:15,margin:"0 0 10px"}}>行車日子（可揀多日）</p>
                  <div style={{display:"flex",flexWrap:"wrap",gap:8,marginBottom:14}}>
                    {[0,1,2,3,4,5,6].map(di=>(
                      <button key={di} type="button" onClick={()=>toggleNewRouteWeekday(di)}
                        style={{padding:"8px 12px",borderRadius:20,border:`1.5px solid ${newRouteWeekdays.includes(di)?TEAL:LIGHT}`,
                          background:newRouteWeekdays.includes(di)?TEAL+"22":WHITE,color:newRouteWeekdays.includes(di)?TEAL:SUB,fontSize:15,cursor:"pointer"}}>
                        週{ROUTE_WEEKDAY_LABELS[di]}
                      </button>
                    ))}
                  </div>
                </>
              )}
              <p style={{color:SUB,fontSize:15,margin:"14px 0 6px"}}>上車地點</p>
              <AddressInput placeholder="例：萬錦某路口" value={newRoutePickup} onChange={setNewRoutePickup} onSelect={setNewRoutePickup}/>
              <p style={{color:SUB,fontSize:15,margin:"14px 0 6px"}}>目的地</p>
              <AddressInput placeholder="例：市中心" value={newRouteDest} onChange={setNewRouteDest} onSelect={setNewRouteDest}/>
              <p style={{color:SUB,fontSize:15,margin:"14px 0 6px"}}>大約出發時間</p>
              <input type="time" value={newRouteTime} onChange={e=>setNewRouteTime(e.target.value)}
                style={{width:"100%",padding:"10px",borderRadius:10,border:`1.5px solid ${LIGHT}`,fontSize:17}}/>
              <p style={{color:SUB,fontSize:15,margin:"14px 0 6px"}}>全車可預留座位（上限）</p>
              <input type="number" min={1} max={8} value={newRouteSeats}
                onChange={e=>setNewRouteSeats(Math.min(8,Math.max(1,parseInt(e.target.value,10)||4)))}
                style={{width:"100%",background:WHITE,border:`1.5px solid ${LIGHT}`,borderRadius:14,padding:"14px 16px",fontSize:18,color:TEXT,marginBottom:12}}/>
              <Card style={{background:"#FFFBEB",border:`1px solid ${GOLD}33`,marginBottom:0}}>
                <p style={{color:TEXT,fontWeight:700,fontSize:16,margin:"0 0 6px"}}>💰 每位乘客收費（加元）</p>
                <p style={{color:SUB,fontSize:14,margin:"0 0 10px",lineHeight:1.5}}>
                  由你決定每位收幾多。
                </p>
                <p style={{color:SUB,fontSize:14,margin:"0 0 10px",lineHeight:1.5}}>
                  下面「GTA 網上共乘參考」係按路程估嘅社群區間，<b>只供參考</b>（假設每位同價模型），唔係平台定價。
                </p>
                <Inp type="number" step="0.5" min={0} placeholder="例：12 或 0（免費／另議）" value={newRouteChargeCad}
                  onChange={e=>setNewRouteChargeCad(e.target.value)} style={{marginBottom:10}}/>
                {newRouteRefBand&&newRoutePricingKm!=null&&(
                  <p style={{color:SUB,fontSize:14,margin:0,lineHeight:1.5}}>
                    📊 <b>GTA 網上共乘參考</b>（約 {newRoutePricingKm.toFixed(1)} km 路程）：每位約 <b style={{color:TEXT}}>${newRouteRefBand.low} – ${newRouteRefBand.high} CAD</b>，中位 <b style={{color:TEAL}}>${newRouteRefBand.mid}</b>
                  </p>
                )}
                {(!newRouteRefBand||newRoutePricingKm==null)&&newRoutePickup.trim()&&newRouteDest.trim()&&(
                  <p style={{color:SUB,fontSize:13,margin:0}}>正在根據地址計算路程同參考價…</p>
                )}
              </Card>
            </Card>
            <Btn label="刊登路線"
              color={newRoutePickup.trim()&&newRouteDest.trim()&&((newRouteKind==="weekly"&&newRouteWeekdays.length>0)||(newRouteKind==="oneOff"&&!!newRouteRunDate))&&!(()=>{const c=parseFloat(String(newRouteChargeCad).replace(",","."));return newRouteChargeCad.trim()===""||Number.isNaN(c)||c<0;})()?TEAL:"#CBD5E1"}
              disabled={!newRoutePickup.trim()||!newRouteDest.trim()||(newRouteKind==="weekly"&&!newRouteWeekdays.length)||(newRouteKind==="oneOff"&&!newRouteRunDate)||(()=>{const c=parseFloat(String(newRouteChargeCad).replace(",","."));return newRouteChargeCad.trim()===""||Number.isNaN(c)||c<0;})()}
              onClick={saveNewDriverRoute}/>
          </Sec>

          <Sec title="我已刊登嘅路線">
            {myDriverRoutes.length===0?<p style={{color:SUB}}>暫未有路線</p>:
            myDriverRoutes.map(r=>(
              <Card key={r.id} style={{marginBottom:10}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:10}}>
                  <Tag label={r.active?"公開中":"已停用"} color={r.active?GREEN:SUB}/>
                  <button type="button" onClick={()=>flipDriverRouteActive(r)}
                    style={{padding:"6px 12px",borderRadius:10,border:`1px solid ${LIGHT}`,background:DC,cursor:"pointer",fontSize:14}}>
                    {r.active?"停用":"重新公開"}
                  </button>
                </div>
                <p style={{color:TEXT,fontWeight:700,margin:"10px 0 4px"}}>{routeScheduleSummary(r)} · 約 {r.departTime} · {r.totalSeats||4} 位</p>
                <p style={{color:SUB,fontSize:15,margin:0}}>📍 {r.pickup}</p>
                <p style={{color:SUB,fontSize:15,margin:"4px 0 0"}}>🏁 {r.dest}</p>
                {(()=>{
                  const pc=r.chargePerPassengerCad;
                  if(pc==null||pc==="")return null;
                  const n=Number(pc);
                  if(Number.isNaN(n)||n<0)return null;
                  return(
                    <p style={{color:TEXT,fontSize:15,margin:"8px 0 0",lineHeight:1.45}}>
                      💵 <b>${n.toFixed(2)} CAD</b>／位（你嘅收費）
                      {r.referenceCarpoolMidCad!=null&&r.referenceCarpoolLowCad!=null&&r.referenceCarpoolHighCad!=null&&(
                        <span style={{display:"block",fontSize:13,color:SUB,marginTop:2}}>
                          網上 GTA 共乘參考：約 ${Number(r.referenceCarpoolLowCad).toFixed(2)}–${Number(r.referenceCarpoolHighCad).toFixed(2)}／位（中位 ${Number(r.referenceCarpoolMidCad).toFixed(2)}）
                        </span>
                      )}
                    </p>
                  );
                })()}
                {(()=>{
                  const meta=driverRouteTripMeta[r.id];
                  const vy=parseInt(vehicleYear,10);
                  const l100=litersPer100km??(vy?heuristicLitersPer100km(vtype,vy):heuristicLitersPer100km(vtype,2018));
                  const isEv=vtype==="電動車";
                  const kmNum=meta&&!meta.loading&&meta.kmNum!=null?meta.kmNum:null;
                  const parts=[];
                  if(meta?.loading){
                    parts.push(<p key="dist0" style={{color:SUB,fontSize:13,margin:"8px 0 0"}}>🛣️ 計算緊全程駕車距離…</p>);
                  }else if(kmNum!=null){
                    parts.push(<p key="dist1" style={{color:SUB,fontSize:14,margin:"8px 0 0"}}>🛣️ 全程駕車約 <b style={{color:TEXT}}>{meta.distText||`${kmNum} km`}</b>（約 {kmNum} km）</p>);
                  }else if(meta){
                    parts.push(<p key="dist2" style={{color:SUB,fontSize:13,margin:"8px 0 0"}}>🛣️ 暫時計唔到全程距離</p>);
                  }
                  if(isEv){
                    parts.push(<p key="ev" style={{color:SUB,fontSize:13,margin:"6px 0 0"}}>⛽ 電動車唔適用汽油費參考</p>);
                    return <>{parts}</>;
                  }
                  const pk=liveGasCadPerL!=null?fuelCadPerKm(l100,liveGasCadPerL):null;
                  const tripCad=kmNum!=null&&pk!=null?estimateTripFuelCad(kmNum,l100,liveGasCadPerL):null;
                  if(liveGasCadPerL!=null&&pk!=null){
                    parts.push(
                      <p key="fuel" style={{color:SUB,fontSize:14,margin:"6px 0 0",lineHeight:1.45}}>
                        <span style={{fontWeight:600,color:TEXT}}>⛽ 今日參考油費</span>：約 <b style={{color:TEAL}}>${pk.toFixed(3)} CAD／km</b>
                        {tripCad!=null&&(
                          <>
                            <span style={{display:"block",marginTop:6,fontSize:15}}>
                              全程估算：<b style={{color:TEAL}}>約 ${tripCad.toFixed(2)} CAD</b>（汽油）
                            </span>
                            <span style={{display:"block",fontSize:12,marginTop:2,lineHeight:1.4}}>
                              基於 ~{l100} L/100km、約 {kmNum} km 路程、OpenVan 加拿大汽油參考價；實際視交通／駕駛習慣而異。
                            </span>
                          </>
                        )}
                        {tripCad==null&&kmNum==null&&!meta?.loading&&(
                          <span style={{display:"block",fontSize:12,marginTop:4}}>路程計好後會顯示全程油費估算。</span>
                        )}
                      </p>
                    );
                  }else if(!myRoutesGasFetched){
                    parts.push(<p key="g0" style={{color:SUB,fontSize:13,margin:"8px 0 0"}}>⛽ 讀取緊今日參考油價…</p>);
                  }else{
                    parts.push(<p key="g1" style={{color:SUB,fontSize:13,margin:"8px 0 0"}}>⛽ 暫時顯示唔到油費（讀唔到參考油價或未設定油耗）</p>);
                  }
                  return <>{parts}</>;
                })()}
                <p style={{color:SUB,fontSize:13,margin:"8px 0 0"}}>
                  {r.routeKind==="oneOff"&&r.runDate
                    ?(r.runDate<todayIsoLocal()
                      ?"單次日子已過（乘客列表唔會再顯示；可停用此路線）"
                      :`出發日：${formatRunDateLabel(r.runDate)}`)
                    :`未來日子例子：${nextRunDatesForWeekdays(r.weekdays||[],14).slice(0,3).map(formatRunDateLabel).join(" · ")||"—"}`}
                </p>
              </Card>
            ))}
          </Sec>

          <Sec title="乘客預留（即將出發）">
            {driverRouteBookings.filter(b=>b.status==="confirmed").length===0?<p style={{color:SUB}}>暫無預留</p>:
            driverRouteBookings.filter(b=>b.status==="confirmed").map(b=>(
              <Card key={b.id} style={{marginBottom:10}}>
                <p style={{color:TEXT,fontWeight:700,margin:0}}>{b.runDate} · 約 {b.departTime||"—"} · 👥 {b.pax} 位</p>
                <p style={{color:SUB,margin:"6px 0 0"}}>乘客：{b.passengerName||"—"}</p>
                <p style={{color:SUB,fontSize:15,margin:"4px 0 0"}}>📍 {b.pickup}</p>
                <p style={{color:SUB,fontSize:15,margin:"2px 0 0"}}>🏁 {b.dest}</p>
                {b.chargePerPassengerCad!=null&&b.chargePerPassengerCad!==""&&!Number.isNaN(Number(b.chargePerPassengerCad))&&(
                  <p style={{color:TEAL,fontSize:14,margin:"6px 0 0",fontWeight:600}}>
                    💵 {Number(b.chargePerPassengerCad).toFixed(2)} CAD／位 × {b.pax} 位 ≈ {(Number(b.chargePerPassengerCad)*b.pax).toFixed(2)} CAD
                  </p>
                )}
                <Btn label="💬 同乘客傾偈" color={TEAL} style={{marginTop:10}}
                  onClick={()=>setRouteChatBooking({
                    id:b.id,
                    peerName:b.passengerName||"乘客",
                    runDate:b.runDate||"",
                    departTime:b.departTime||"",
                    pax:b.pax||1,
                    pickup:b.pickup||"",
                    dest:b.dest||"",
                  })}/>
              </Card>
            ))}
          </Sec>
        </Wrap>
        <BottomNav active={navTab} onChange={handleNavTab} items={bottomNavItems} bottomInset={bottomNavLift}/>
      </div>
    );

    if(settingsSub==="schedule")return(
      <div style={{minHeight:"100vh",background:BG,paddingBottom:contentPadBottom}}>
        <Bar title={t("bar.availability")} onBack={()=>setSettingsSub(null)}/>
        <Wrap>
          <Card style={{background:"#F0FDFA",border:`1px solid ${TEAL}33`}}>
            <p style={{color:TEAL,fontSize:16,margin:0}}>📅 設定你平時有空嘅時段，方便預約Carpool自動配對</p>
          </Card>
          {schedule.map((s,i)=>(
            <Card key={i}>
              <div style={{display:"flex",gap:8,alignItems:"center",marginBottom:8}}>
                <select value={s.day} onChange={e=>updateScheduleSlot(i,"day",parseInt(e.target.value))}
                  style={{flex:1,padding:"8px",borderRadius:8,border:`1px solid ${LIGHT}`,fontSize:17}}>
                  {DAYS.map((d,di)=><option key={di} value={di}>{d}</option>)}
                </select>
                <button onClick={()=>removeScheduleSlot(i)} style={{background:RED+"15",border:`1px solid ${RED}33`,color:RED,borderRadius:8,padding:"6px 10px",fontSize:15,cursor:"pointer"}}>✕</button>
              </div>
              <div style={{display:"flex",gap:8}}>
                <input type="time" value={s.start} onChange={e=>updateScheduleSlot(i,"start",e.target.value)} style={{flex:1,padding:"8px",borderRadius:8,border:`1px solid ${LIGHT}`,fontSize:17}}/>
                <span style={{color:SUB,alignSelf:"center"}}>至</span>
                <input type="time" value={s.end} onChange={e=>updateScheduleSlot(i,"end",e.target.value)} style={{flex:1,padding:"8px",borderRadius:8,border:`1px solid ${LIGHT}`,fontSize:17}}/>
              </div>
            </Card>
          ))}
          <Btn label="➕ 新增時段" color={DC} textColor={TEAL} style={{border:`1px solid ${TEAL}44`,boxShadow:"none",marginBottom:12}} onClick={addScheduleSlot}/>
          <Btn label="儲存" onClick={saveDriverProfile}/>
        </Wrap>
        <BottomNav active={navTab} onChange={handleNavTab} items={bottomNavItems} bottomInset={bottomNavLift}/>
      </div>
    );

    return(
      <div style={{minHeight:"100vh",background:BG,paddingBottom:contentPadBottom}}>
        <Bar title={t("bar.settings")} onBack={()=>handleNavTab("orders")}/>
        <Wrap>
          <LanguagePickerCard/>
          {[
            {key:"profile",icon:"✏️",label:"編輯資料",sub:"簡介、興趣、座椅、寵物偏好"},
            {key:"schedule",icon:"📅",label:"可用時段",sub:`已設定 ${schedule.length} 個時段`},
            {key:"my_routes",icon:"📍",label:"固定路線（乘客預留）",sub:`已刊登 ${myDriverRoutes.filter(r=>r.active).length} 條路線`},
          ].map(item=>(
            <Card key={item.key} style={{cursor:"pointer"}} onClick={()=>setSettingsSub(item.key)}>
              <div style={{display:"flex",alignItems:"center",gap:12}}>
                <span style={{fontSize:20}}>{item.icon}</span>
                <div style={{flex:1}}>
                  <p style={{color:TEXT,fontWeight:600,fontSize:17,margin:0}}>{item.label}</p>
                  <p style={{color:SUB,fontSize:15,margin:0}}>{item.sub}</p>
                </div>
                <span style={{color:SUB}}>→</span>
              </div>
            </Card>
          ))}
          <Btn label="登出" color={RED} onClick={()=>{auth.signOut();onBack();}}/>
        </Wrap>
        <BottomNav active={navTab} onChange={handleNavTab} items={bottomNavItems} bottomInset={bottomNavLift}/>
      </div>
    );
  }

  // ── HISTORY ────────────────────────────────────────────────────────
  if(step==="history")return(
    <div style={{minHeight:"100vh",background:BG,paddingBottom:contentPadBottom}}>
      <Bar title={t("bar.driverHistory")} onBack={()=>handleNavTab("orders")}/>
      <Wrap>
        {historyLoading?<div style={{textAlign:"center",padding:40}}><div style={{fontSize:30,animation:"go-spin 1s linear infinite"}}>🔄</div></div>:
        history.length===0?<Empty icon="📭" msg="暫無Carpool紀錄"/>:
        history.map(o=>(
          <Card key={o.id} style={{cursor:["scheduled_pending","grabbed","awaiting_passenger"].includes(o.status)?"pointer":"default"}}
            onClick={()=>{
              if(["scheduled_pending","grabbed","awaiting_passenger"].includes(o.status))openDriverHistoryOrder(o);
            }}>
            <div style={{display:"flex",justifyContent:"space-between",marginBottom:8}}>
              <span style={{color:TEXT,fontSize:16,fontWeight:600}}>{formatDateTime(o.createdAt)}</span>
              <StatusBadge status={o.status}/>
            </div>
            <div style={{background:DC,borderRadius:10,padding:"8px 12px",marginBottom:8}}>
              <p style={{color:SUB,fontSize:15,margin:"0 0 3px"}}>📍 {o.pickup}</p>
              <p style={{color:SUB,fontSize:15,margin:0}}>🏁 {o.dest}</p>
            </div>
            <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
              <Tag label={`👤 ${o.passengerName}`} color={SUB}/>
              {o.distKm!=null&&o.distKm!==""&&<Tag label={`📏 ${o.distKm}km`} color={SUB}/>}
              {o.rating&&<Tag label={`⭐ ${o.rating}星`} color={GOLD}/>}
            </div>
          </Card>
        ))}
      </Wrap>
      <BottomNav active={navTab} onChange={handleNavTab} items={bottomNavItems} bottomInset={bottomNavLift}/>
    </div>
  );

  // ══════════════════════════════════════════════════════════════════════
  // ── ORDERS LIST ────────────────────────────────────────────────────
  // ══════════════════════════════════════════════════════════════════════

  if(step==="orders")return(
    <div style={{minHeight:"100vh",background:BG,paddingBottom:contentPadBottom}}>
      <Bar title={t("bar.driverPlatform")} onBack={onBack}
        right={<div onClick={()=>setOnline(!online)} style={{
          background:online?"#ECFDF5":"#F1F5F9",border:`1px solid ${online?GREEN:LIGHT}`,
          borderRadius:20,padding:"5px 14px",cursor:"pointer",fontSize:14,
          color:online?GREEN:SUB,fontWeight:700}}>{online?"● 在線":"○ 離線"}</div>}/>

      <TabBar tabs={[{key:"now",Icon:Zap,label:t("tab.nowRides")},{key:"scheduled",Icon:CalendarDays,label:t("tab.scheduledRides")}]}
        active={orderTab} onChange={setOrderTab}/>

      <Wrap>
        <Card style={{cursor:"pointer",marginBottom:8,background:"#F0FDFA",border:`1px solid ${TEAL}33`}} onClick={()=>{
          handleNavTab("settings");
          setTimeout(()=>setSettingsSub("my_routes"),0);
        }}>
          <p style={{color:TEAL,fontWeight:800,fontSize:17,margin:0}}>📍 固定路線 · 乘客預留座位</p>
          <p style={{color:SUB,fontSize:15,margin:"6px 0 0",lineHeight:1.45}}>
            設定每週路線同總座位；乘客即時睇剩位並預約。已刊登 <b>{myDriverRoutes.filter(r=>r.active).length}</b> 條。
          </p>
        </Card>

        {driverUpcoming&&(
          <Card onClick={()=>{
            const d=driverUpcoming;
            setGrabbed({
              id:d.id,passengerId:d.passengerId||null,p:d.passengerName,pu:d.pickup,dt:d.dest,
              fare:d.fare,pax:d.pax,phone:d.phone||"",stops:d.stops||[],
              pAvatar:d.passengerAvatar||null,pGender:d.passengerGender||"",
              _orderStatus:d.status||"scheduled_pending",scheduledTime:d.scheduledTime,isScheduled:d.isScheduled,
            });
            setEtaData(null);
            setArrived(false);
            setStep("grabbed");
          }} style={{cursor:"pointer",background:"#EEF2FF",border:"1px solid #818CF844",marginBottom:4}}>
            <p style={{color:"#3730A3",fontWeight:700,margin:0,fontSize:17}}>📅 你有預約已配對 · 待出發</p>
            <p style={{color:SUB,fontSize:16,margin:"8px 0 0",lineHeight:1.45}}>
              出發時間：<b style={{color:TEXT}}>{driverUpcoming.scheduledTime?formatDateTime(driverUpcoming.scheduledTime):"—"}</b>
              <span style={{display:"block",marginTop:6}}>撳此打開詳情同聊天</span>
            </p>
          </Card>
        )}
        <Card>
          <div style={{display:"flex",alignItems:"center",gap:12}}>
            <div style={{width:52,height:52,borderRadius:"50%",background:selfiePreview?"transparent":DC,overflow:"hidden",
              display:"flex",alignItems:"center",justifyContent:"center",fontSize:26,border:`2px solid ${LIGHT}`,flexShrink:0}}>
              {selfiePreview?<img src={selfiePreview} style={{width:"100%",height:"100%",objectFit:"cover"}} alt="me"/>:"👤"}
            </div>
            <div>
              <p style={{color:TEXT,fontWeight:700,fontSize:18,margin:0}}>{dname}</p>
              <StarDisplay rating={avgRating} count={ratingCount}/>
              <div style={{display:"flex",gap:4,flexWrap:"wrap",marginTop:4}}>
                {langs.map(l=>(<span key={l} style={{background:DC,border:`1px solid ${LIGHT}`,borderRadius:20,padding:"2px 8px",fontSize:14,color:SUB}}>
                  {l==="廣東話"?"🇭🇰":l==="普通話"?"🇨🇳":"🇬🇧"} {l}</span>))}
              </div>
              {occupation&&<div style={{marginTop:4}}><Tag label={`💼 ${occupation}`} color={GOLD}/></div>}
            </div>
          </div>
        </Card>

        {(()=>{
          const filtered=orders.filter(o=>orderTab==="scheduled"?o.isScheduled:!o.isScheduled);
          return filtered.length===0?(
            <Empty icon="🕐" msg={orderTab==="scheduled"?"暫時冇預約Carpool":"暫時冇新Carpool請求，等緊..."}/>
          ):filtered.map(o=>(
            <Card key={o.id} style={{cursor:o.orderStatus==="awaiting_driver"?"default":"pointer"}}
              onClick={o.orderStatus==="awaiting_driver"?undefined:()=>grab(o)}>
              {o.passengerId&&(
                <button type="button" onClick={e=>{e.stopPropagation();setPeerModal({role:"passenger",uid:o.passengerId});}}
                  style={{width:"100%",marginBottom:10,background:"#EEF2FF",border:"1px solid #818CF844",borderRadius:10,padding:"8px 10px",fontSize:15,fontWeight:700,color:"#3730A3",cursor:"pointer"}}>
                  👤 睇乘客完整檔案（配對前預覽）
                </button>
              )}
              <div style={{display:"flex",justifyContent:"space-between",marginBottom:8}}>
                <div style={{display:"flex",alignItems:"center",gap:8}}>
                  <button type="button" onClick={e=>{e.stopPropagation();if(o.passengerId)setPeerModal({role:"passenger",uid:o.passengerId});}}
                    style={{border:"none",background:"transparent",padding:0,cursor:o.passengerId?"pointer":"default",flexShrink:0}} title="睇檔案">
                    <div style={{width:36,height:36,borderRadius:"50%",background:DC,overflow:"hidden",display:"flex",alignItems:"center",justifyContent:"center",fontSize:18}}>
                      {o.pAvatar?<img src={o.pAvatar} style={{width:"100%",height:"100%",objectFit:"cover"}} alt=""/>:"👤"}
                    </div>
                  </button>
                  <div>
                    <p style={{color:TEXT,fontWeight:600,margin:0}}>{o.p}</p>
                    {o.pOccupation&&<span style={{color:GOLD,fontSize:14}}>💼 {o.pOccupation}</span>}
                  </div>
                </div>
                <div style={{display:"flex",flexDirection:"column",alignItems:"flex-end",gap:6}}>
                  {o.orderStatus==="awaiting_driver"&&(
                    <Tag label="💌 乘客揀咗你" color="#818CF8"/>
                  )}
                  {o.isScheduled&&o.scheduledTime?(
                    <span style={{color:"#818CF8",fontSize:14,background:"#818CF822",padding:"3px 10px",borderRadius:20,height:"fit-content",fontWeight:600}}>
                      📅 {formatDateTime(o.scheduledTime)}
                    </span>
                  ):(
                    <span style={{color:WHITE,fontSize:14,background:TEAL,padding:"3px 10px",borderRadius:20,height:"fit-content"}}>剛剛</span>
                  )}
                </div>
              </div>
              {/* Show passenger interests for Coffee Chat matching */}
              {(o.pExpertise?.length>0||o.pHobbies?.length>0)&&(
                <div style={{marginBottom:8}}>
                  <InterestTags occupation="" expertise={o.pExpertise} hobbies={o.pHobbies}/>
                </div>
              )}
              <div style={{display:"flex",gap:5,flexWrap:"wrap",marginBottom:8}}>
                <Tag label={`👥 ${o.pax}人`} color={TEAL}/>
                {o.pickupDistText&&<Tag label={`📍距離你約 ${o.pickupDistText}`} color="#4F46E5"/>}
                {o.pGender&&<Tag label={`⚧ ${P_GENDER_LABEL[o.pGender]||o.pGender}`} color={SUB}/>}
                {o.hasChild&&<Tag label="👶 有細路" color={GOLD}/>}
                {o.hasPet&&<Tag label="🐾 有寵物" color={WARN}/>}
                {o.stops?.length>0&&<Tag label={`📌 ${o.stops.length}個中途站`} color={SUB}/>}
              </div>
              <div style={{background:DC,borderRadius:10,padding:"8px 12px",marginBottom:10}}>
                <button type="button" onClick={e=>{e.stopPropagation();openGoogleMapsNav(o.pu);}}
                  style={{display:"block",width:"100%",textAlign:"left",background:"transparent",border:"none",padding:0,cursor:"pointer",color:TEAL,fontSize:15,margin:"0 0 6px",fontWeight:600,lineHeight:1.4}}>
                  📍 {o.pu} <span style={{color:SUB,fontWeight:500}}>→ 🗺️ 導航</span>
                </button>
                {o.stops?.map((s,i)=>(
                  <button key={i} type="button" onClick={e=>{e.stopPropagation();openGoogleMapsNav(s.address);}}
                    style={{display:"block",width:"100%",textAlign:"left",background:"transparent",border:"none",padding:0,cursor:"pointer",color:TEAL,fontSize:15,margin:"0 0 6px",fontWeight:600,lineHeight:1.4}}>
                    📌 {s.address} <span style={{color:SUB,fontWeight:500}}>→ 🗺️</span>
                  </button>
                ))}
                <button type="button" onClick={e=>{e.stopPropagation();openGoogleMapsNav(o.dt);}}
                  style={{display:"block",width:"100%",textAlign:"left",background:"transparent",border:"none",padding:0,cursor:"pointer",color:TEAL,fontSize:15,margin:0,fontWeight:600,lineHeight:1.4}}>
                  🏁 {o.dt} <span style={{color:SUB,fontWeight:500}}>→ 🗺️ 導航</span>
                </button>
              </div>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:10}}>
                <div style={{display:"flex",gap:6,flexWrap:"wrap",alignItems:"center"}}>
                  <Tag label={`📏 ${o.dist}`} color={SUB}/>
                  {(()=>{
                    const km=o.distKmNum;
                    const vy=parseInt(vehicleYear,10);
                    const l100=vtype==="電動車"?null:(litersPer100km ?? (vy?heuristicLitersPer100km(vtype,vy):heuristicLitersPer100km(vtype,2018)));
                    if(!km||!l100||!liveGasCadPerL)return null;
                    const cad=estimateTripFuelCad(km,l100,liveGasCadPerL);
                    if(cad==null)return null;
                    return <Tag label={`⛽ 全程 ~$${cad.toFixed(2)}（即時參考油價）`} color={WARN}/>;
                  })()}
                </div>
                {o.orderStatus==="awaiting_driver"?(
                  <div style={{display:"flex",gap:8,width:"100%",justifyContent:"flex-end"}}>
                    <button type="button" onClick={e=>{e.stopPropagation();declinePassengerInvite(o);}}
                      style={{background:RED,color:WHITE,border:"none",borderRadius:10,padding:"8px 14px",fontSize:15,fontWeight:700,cursor:"pointer"}}>婉拒</button>
                    <button type="button" onClick={e=>{e.stopPropagation();acceptPassengerInvite(o);}}
                      style={{background:TEAL,color:WHITE,border:"none",borderRadius:10,padding:"8px 18px",fontSize:16,fontWeight:700,cursor:"pointer",boxShadow:`0 2px 8px ${TEAL}44`}}>✅ 接受邀請</button>
                  </div>
                ):(
                  <button onClick={e=>{e.stopPropagation();grab(o);}}
                    style={{background:TEAL,color:WHITE,border:"none",borderRadius:10,padding:"8px 18px",fontSize:16,fontWeight:700,cursor:"pointer",boxShadow:`0 2px 8px ${TEAL}44`}}>接受Carpool →</button>
                )}
              </div>
            </Card>
          ));
        })()}
      </Wrap>
      <PeerProfileModal open={peerModal!=null} role={peerModal?.role} userId={peerModal?.uid} onClose={()=>setPeerModal(null)}/>
      <BottomNav active={navTab} onChange={handleNavTab} items={bottomNavItems} bottomInset={bottomNavLift}/>
    </div>
  );

  // ══════════════════════════════════════════════════════════════════════
  // ── PENDING PASSENGER CONFIRM ─────────────────────────────────────
  // ══════════════════════════════════════════════════════════════════════

  if(step==="pending_passenger"&&grabbed){
    return(
      <div style={{minHeight:"100vh",background:BG,paddingBottom:bottomNavLift}}>
        <Bar title={t("bar.waitPassengerConfirm")} onBack={back}/>
        <Wrap>
          {chatUnreadCount>0&&(
            <button type="button" onClick={focusWaitingChat}
              style={{marginBottom:10,background:"#ECFDF5",border:`1px solid ${GREEN}55`,color:GREEN,borderRadius:999,padding:"8px 14px",fontSize:14,fontWeight:700,cursor:"pointer"}}>
              💬 新訊息 {chatUnreadCount}
            </button>
          )}
          <Card style={{background:"#FFFBEB",border:`1px solid ${GOLD}33`}}>
            <p style={{color:TEXT,fontSize:17,fontWeight:700,margin:0}}>⏳ 你已接單，等候乘客確認</p>
            <p style={{color:SUB,fontSize:16,margin:"8px 0 0",lineHeight:1.5}}>乘客撳「確認配對」後會正式開始行程；而家已可先用聊天溝通集合安排。</p>
          </Card>
          <Card style={{marginTop:14}}>
            <button type="button" disabled={!grabbed.passengerId}
              onClick={()=>grabbed.passengerId&&setPeerModal({role:"passenger",uid:grabbed.passengerId})}
              style={{width:"100%",textAlign:"left",background:"transparent",border:"none",padding:0,cursor:grabbed.passengerId?"pointer":"default"}}>
              <p style={{color:SUB,fontSize:16,margin:0}}>👤 {grabbed.p}</p>
              {grabbed.passengerId&&<p style={{color:TEAL,fontSize:15,margin:"8px 0 0",fontWeight:700}}>撳此睇乘客完整檔案 →</p>}
            </button>
            <p style={{color:TEXT,fontSize:16,marginTop:8}}>📍 {grabbed.pu}</p>
            <p style={{color:TEXT,fontSize:16,marginTop:4}}>🏁 {grabbed.dt}</p>
            <div style={{marginTop:10,display:"flex",gap:6,flexWrap:"wrap"}}>
              {grabbed.dist&&<Tag label={`📏 ${grabbed.dist}`} color={SUB}/>}
              {(()=>{
                const km=parseFloat(grabbed.distKm)||grabbed.distKmNum||0;
                const l100=grabbed.driverLitersPer100km ?? (vtype!=="電動車"?(litersPer100km ?? (parseInt(vehicleYear,10)?heuristicLitersPer100km(vtype,parseInt(vehicleYear,10)):heuristicLitersPer100km(vtype,2018))):null);
                if(!km||!l100||!liveGasCadPerL)return null;
                const cad=estimateTripFuelCad(km,l100,liveGasCadPerL);
                if(cad==null)return null;
                return <Tag label={`⛽ 全程 ~$${cad.toFixed(2)}（參考）`} color={WARN}/>;
              })()}
            </div>
          </Card>
          <div ref={waitingChatRef} onClick={()=>setChatUnreadCount(0)}>
            <ChatBox
              messages={chatMessages}
              onSend={sendChatMessage}
              myId={auth.currentUser?.uid}
              myName={dname}
              h={180}
            />
          </div>
          <Btn label="撤回接單" color={RED} style={{marginTop:16}} onClick={withdrawDriverOffer}/>
        </Wrap>
        <PeerProfileModal open={peerModal!=null} role={peerModal?.role} userId={peerModal?.uid} onClose={()=>setPeerModal(null)}/>
      </div>
    );
  }

  // ══════════════════════════════════════════════════════════════════════
  // ── GRABBED ────────────────────────────────────────────────────────
  // ══════════════════════════════════════════════════════════════════════

  if(step==="grabbed"&&grabbed){
    if(recDone)return(
      <div className="go-fade" style={{minHeight:"100vh",background:BG,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",padding:24,paddingBottom:24+bottomNavLift}}>
        <div style={{fontSize:64,marginBottom:16}}>🎉</div>
        <p style={{color:TEXT,fontSize:22,fontWeight:700,marginBottom:28}}>多謝推薦！</p>
        <Btn label="繼續接Carpool" color={TEAL} style={{maxWidth:200}}
          onClick={()=>{setRecDone(false);setGrabbed(null);setArrived(false);setEtaData(null);setChatMessages([]);setStep("orders");}}/>
      </div>
    );

    const passengerPhone=(grabbed.phone||"").replace(/\D/g,"");
    const passengerPhoneFull=passengerPhone.startsWith("1")?passengerPhone:"1"+passengerPhone;
    const du=grabbed._orderStatus==="scheduled_pending";

    return(
      <div style={{minHeight:"100vh",background:BG,paddingBottom:bottomNavLift}}>
        <Bar title={du?t("bar.tripUpcoming"):t("bar.tripActive")} onBack={back}/>
        <Wrap>
          {du?(
            <div style={{background:"#EEF2FF",border:"1px solid #818CF844",borderRadius:12,padding:"12px 16px",marginBottom:14}}>
              <p style={{color:"#3730A3",fontWeight:700,margin:0}}>📅 預約已配對 · 待出發</p>
              <p style={{color:SUB,fontSize:16,margin:"8px 0 0",lineHeight:1.45}}>
                出發時間：<b style={{color:TEXT}}>{grabbed.scheduledTime?formatDateTime(grabbed.scheduledTime):"—"}</b>
              </p>
              <p style={{color:SUB,fontSize:15,margin:"8px 0 0",lineHeight:1.45}}>
                到咗預約時間會自動進入「進行中」。可返回列表，隨時喺頂部卡片或「紀錄」再入嚟睇。
              </p>
              <Btn label="返回Carpool列表" color={TEAL} style={{marginTop:12}} onClick={()=>setStep("orders")}/>
            </div>
          ):(
            <div style={{background:"#ECFDF5",border:`1px solid ${GREEN}33`,borderRadius:12,padding:"12px 16px",marginBottom:14}}>
              <p style={{color:GREEN,fontWeight:700,margin:0}}>✅ 你已接受呢個Carpool請求！</p>
              <p style={{color:"#6EE7B7",fontSize:15,margin:"4px 0 0"}}>請盡快前往上車地點</p>
              <Btn label="🗺️ Google Maps 導航去上車點" color={GREEN} style={{marginTop:12}}
                onClick={()=>openGoogleMapsNav(grabbed.pu)}/>
            </div>
          )}

          <div style={{borderRadius:14,overflow:"hidden",border:`2px solid ${TEAL}44`}}>
            <MapView pickupAddr={grabbed.pu} h={200}/>
            <button type="button" onClick={()=>openGoogleMapsNav(grabbed.pu)}
              style={{width:"100%",border:"none",textAlign:"center",color:TEAL,fontSize:15,fontWeight:600,padding:"12px 10px",background:"#F0FDFA",cursor:"pointer",lineHeight:1.4}}>
              👆 撳此處用 Google Maps{du?" 預覽上車點":" 導航去上車點"}
            </button>
          </div>

          <Card style={{marginTop:14}}>
            <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:10}}>
              <div style={{width:40,height:40,borderRadius:"50%",background:DC,overflow:"hidden",display:"flex",alignItems:"center",justifyContent:"center",fontSize:20}}>
                {grabbed.pAvatar?<img src={grabbed.pAvatar} style={{width:"100%",height:"100%",objectFit:"cover"}} alt=""/>:"👤"}
              </div>
              <div>
                <p style={{color:TEXT,fontWeight:600,fontSize:18,margin:0}}>{grabbed.p}</p>
                <div style={{display:"flex",gap:5,flexWrap:"wrap",marginTop:6}}>
                  <Tag label={`👥 ${grabbed.pax}人`} color={TEAL}/>
                  {grabbed.pGender&&<Tag label={`⚧ ${P_GENDER_LABEL[grabbed.pGender]||grabbed.pGender}`} color={SUB}/>}
                </div>
                {grabbed.passengerId&&(
                  <button type="button" onClick={()=>setPeerModal({role:"passenger",uid:grabbed.passengerId})}
                    style={{marginTop:10,background:"transparent",border:"none",color:TEAL,fontSize:15,fontWeight:700,cursor:"pointer",textDecoration:"underline",padding:0}}>
                    👤 睇乘客完整檔案
                  </button>
                )}
              </div>
            </div>
            <div style={{display:"flex",gap:5,flexWrap:"wrap",marginBottom:12}}>
              {grabbed.stops?.length>0&&<Tag label={`📌 ${grabbed.stops.length}個中途站`} color={SUB}/>}
            </div>
            <div style={{background:DC,borderRadius:10,padding:12,marginBottom:12}}>
              <button type="button" onClick={()=>openGoogleMapsNav(grabbed.pu)}
                style={{display:"flex",width:"100%",justifyContent:"space-between",alignItems:"flex-start",gap:10,marginBottom:8,background:"transparent",border:"none",padding:"6px 0",cursor:"pointer",textAlign:"left"}}>
                <span style={{color:SUB,fontSize:16,flexShrink:0}}>📍 上車</span>
                <span style={{color:TEAL,fontSize:16,fontWeight:600,textDecoration:"underline",lineHeight:1.35}}>{grabbed.pu} 🗺️</span>
              </button>
              {grabbed.stops?.map((s,i)=>(
                <button key={i} type="button" onClick={()=>openGoogleMapsNav(s.address)}
                  style={{display:"flex",width:"100%",justifyContent:"space-between",alignItems:"flex-start",gap:10,marginBottom:8,background:"transparent",border:"none",padding:"6px 0",cursor:"pointer",textAlign:"left"}}>
                  <span style={{color:SUB,fontSize:16,flexShrink:0}}>📌 中途{i+1}</span>
                  <span style={{color:TEAL,fontSize:16,fontWeight:600,textDecoration:"underline",lineHeight:1.35}}>{s.address} 🗺️</span>
                </button>
              ))}
              <button type="button" onClick={()=>openGoogleMapsNav(grabbed.dt)}
                style={{display:"flex",width:"100%",justifyContent:"space-between",alignItems:"flex-start",gap:10,marginBottom:8,background:"transparent",border:"none",padding:"6px 0",cursor:"pointer",textAlign:"left"}}>
                <span style={{color:SUB,fontSize:16,flexShrink:0}}>🏁 目的地</span>
                <span style={{color:TEAL,fontSize:16,fontWeight:600,textDecoration:"underline",lineHeight:1.35}}>{grabbed.dt} 🗺️</span>
              </button>
              <div style={{marginTop:4,paddingTop:8,borderTop:`1px solid ${LIGHT}`}}>
                <span style={{color:SUB,fontSize:16,display:"block",marginBottom:4}}>⛽ 汽油費估算（即時參考油價）</span>
                <span style={{color:TEXT,fontSize:16,fontWeight:600}}>
                  {(()=>{
                    const km=parseFloat(grabbed.distKm)||grabbed.distKmNum||0;
                    const l100=grabbed.driverLitersPer100km ?? (vtype!=="電動車"?(litersPer100km ?? (parseInt(vehicleYear,10)?heuristicLitersPer100km(vtype,parseInt(vehicleYear,10)):heuristicLitersPer100km(vtype,2018))):null);
                    if(vtype==="電動車")return"—（電動車）";
                    if(!km||!l100)return"—（缺路程或油耗資料）";
                    if(!liveGasCadPerL)return"—（暫時讀唔到油價）";
                    const cad=estimateTripFuelCad(km,l100,liveGasCadPerL);
                    return cad!=null?`約 $${cad.toFixed(2)} CAD（~${l100} L/100km × ${km} km）`:"—";
                  })()}
                </span>
                <p style={{color:SUB,fontSize:13,margin:"6px 0 0",lineHeight:1.45}}>實際分擔請與乘客協商；油價來源：OpenVan 公開參考數據。</p>
              </div>
              {etaData&&!du&&(
                <div style={{display:"flex",justifyContent:"space-between",marginBottom:5}}>
                  <span style={{color:SUB,fontSize:16}}>⏱️ 預計到達</span>
                  <span style={{color:TEAL,fontSize:16,fontWeight:600}}>{etaData.durText}</span>
                </div>
              )}
            </div>

            <div style={{display:"flex",gap:8,marginBottom:10}}>
              <a href={`tel:+${passengerPhoneFull}`} style={{flex:1,background:DC,border:`1px solid ${LIGHT}`,borderRadius:12,padding:"12px 0",fontSize:16,color:TEXT,textAlign:"center",textDecoration:"none",display:"block",fontWeight:600}}>📞 致電</a>
              <button onClick={()=>{
                window.open(`https://wa.me/${passengerPhoneFull}?text=${encodeURIComponent("你好，我係你嘅八達Go Carpool車主，快到喇！")}`,"_blank");
              }} style={{flex:1,background:DC,border:`1px solid ${LIGHT}`,borderRadius:12,padding:"12px 0",fontSize:16,color:TEXT,fontWeight:600,cursor:"pointer"}}>💬 WhatsApp</button>
            </div>

            {!du&&(
              <>
                <Btn label={arrived?"✅ 已到達":"我到咗！"} color={arrived?GREEN:TEAL} onClick={()=>setArrived(true)}/>
                {arrived&&(
                  <div className="go-fade" style={{marginTop:12,background:"#ECFDF5",border:`1px solid ${GREEN}33`,borderRadius:12,padding:"12px 14px"}}>
                    <p style={{color:GREEN,fontWeight:600,marginBottom:12}}>🤝 油費分擔請私下同乘客協商</p>
                    <div style={{display:"flex",gap:10}}>
                      {[{i:"👍",l:"推薦乘客",c:GREEN},{i:"🚩",l:"舉報",c:RED}].map((o,idx)=>(
                        <div key={idx} onClick={()=>setRecDone(true)} style={{flex:1,background:WHITE,border:`2px solid ${o.c}33`,borderRadius:12,padding:"12px 0",textAlign:"center",cursor:"pointer"}}>
                          <div style={{fontSize:24}}>{o.i}</div>
                          <div style={{color:o.c,fontSize:15,fontWeight:600,marginTop:4}}>{o.l}</div>
                        </div>
                      ))}
                    </div>
                    <div style={{marginTop:10}}>
                      <Btn label="完成Carpool" color={TEAL} onClick={completeTrip}/>
                    </div>
                  </div>
                )}
              </>
            )}
          </Card>

          <ChatBox
            messages={chatMessages}
            onSend={sendChatMessage}
            myId={auth.currentUser?.uid}
            myName={dname}
            h={200}
          />
        </Wrap>
        <PeerProfileModal open={peerModal!=null} role={peerModal?.role} userId={peerModal?.uid} onClose={()=>setPeerModal(null)}/>
      </div>
    );
  }

  return null;
};

export default Driver;
